import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import FloatingField from '../components/FloatingField';
import {
  AlertTriangle,
  BriefcaseBusiness,
  Building2,
  Calendar,
  Check,
  CheckCircle,
  ClipboardList,
  ChevronDown,
  Clock,
  Droplets,
  Download,
  ExternalLink,
  Filter,
  Home,
  MapPin,
  MessageCircle,
  Package,
  Phone,
  Printer,
  RotateCcw,
  Ruler,
  Scale,
  SearchX,
  Search,
  ShoppingCart,
  Sofa,
  Star,
  Truck,
  User,
  UserCheck,
  UserCog,
  UserMinus,
  Wrench,
  Zap,
  X,
  XCircle
} from 'lucide-react';
import { API_URL } from '../config';
import Dropdown from '../components/Dropdown';
import DatePicker from '../components/DatePicker';
import ModalShell from '../components/ModalShell';
import useToast from '../hooks/useToast';
import { ModalListSkeleton } from '../components/Skeleton';
import useModalBackButton from '../hooks/useModalBackButton';
import { formatTimezoneDate } from '../utils/timezone';
import { STAGES_CONFIG } from '../utils/stages';
import { fetchProtectedBlobWithResponse } from '../utils/protectedMedia';
import { haptic } from '../utils/vibration';
import { useDryCleaningEnabled } from '../utils/featureFlags';

const STORAGE_KEY = 'orders_log_view_v2';
const UNASSIGN_ASSIGNEE_VALUE = '__unassign__';
const ALL_BULK_ASSIGNEES_VALUE = '__all_bulk_assignees__';
const MAX_RANGE_DAYS = 90;
const FLOW_STAGE_NAMES = ['washing_laundry', 'washing_cleaning'];
const ORDER_LOG_PAGE_SIZE = 30;
const EMPTY_ORDER_LOG_COUNTS = {
  all: null,
  active: null,
  deferred: null,
  unassigned: null,
  repeat: null,
  rework: null,
  completed: null,
  cancelled: null
};

const buildAllBulkAssigneesTarget = (stage) => {
  if (!stage || stage.stage_name === 'all' || !Array.isArray(stage.assignees)) return null;

  const ordersById = new Map();
  stage.assignees.forEach((assignee) => {
    (assignee.orders || []).forEach((order) => {
      const existing = ordersById.get(order.id);
      if (!existing) {
        ordersById.set(order.id, {
          ...order,
          stage_names: [...(order.stage_names || [])],
          stage_labels: [...(order.stage_labels || [])]
        });
        return;
      }
      (order.stage_names || []).forEach((stageName, index) => {
        if (!existing.stage_names.includes(stageName)) {
          existing.stage_names.push(stageName);
          existing.stage_labels.push(order.stage_labels?.[index] || stageName);
        }
      });
    });
  });

  const orders = Array.from(ordersById.values()).sort((left, right) => (
    String(right.created_date || '').localeCompare(String(left.created_date || ''))
  ));
  return {
    id: null,
    name: 'Все исполнители',
    assignments_count: Number(stage.assignments_count || 0),
    orders_count: orders.length,
    orders,
    all_assignees: true
  };
};

const QUICK_FILTERS = [
  { key: 'all', label: 'Все' },
  { key: 'active', label: 'В работе' },
  { key: 'unassigned', label: 'Нераспр.' },
  { key: 'deferred', label: 'Отложены' },
  { key: 'repeat', label: 'Повтор' },
  { key: 'rework', label: 'Доработка' },
  { key: 'completed', label: 'Завершены' },
  { key: 'cancelled', label: 'Отменены' }
];

const QUICK_FILTER_STYLES = {
  all: {
    active: 'border-gray-400 bg-gray-500 text-white dark:border-gray-500 dark:bg-gray-300 dark:text-gray-900',
    idle: 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'
  },
  active: {
    active: 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300',
    idle: 'border-gray-200 bg-white text-gray-700 hover:border-blue-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'
  },
  unassigned: {
    active: 'border-gray-300 bg-gray-100 text-gray-700 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-200',
    idle: 'border-gray-200 bg-white text-gray-700 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'
  },
  deferred: {
    active: 'border-amber-300 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950/40 dark:text-amber-300',
    idle: 'border-gray-200 bg-white text-gray-700 hover:border-amber-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'
  },
  repeat: {
    active: 'border-orange-300 bg-orange-50 text-orange-700 dark:border-orange-800 dark:bg-orange-950/40 dark:text-orange-300',
    idle: 'border-gray-200 bg-white text-gray-700 hover:border-orange-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'
  },
  rework: {
    active: 'border-violet-300 bg-violet-50 text-violet-700 dark:border-violet-800 dark:bg-violet-950/40 dark:text-violet-300',
    idle: 'border-gray-200 bg-white text-gray-700 hover:border-violet-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'
  },
  completed: {
    active: 'border-green-300 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950/40 dark:text-green-300',
    idle: 'border-gray-200 bg-white text-gray-700 hover:border-green-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'
  },
  cancelled: {
    active: 'border-red-300 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-950/40 dark:text-red-300',
    idle: 'border-gray-200 bg-white text-gray-700 hover:border-red-200 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200'
  }
};

const ORDER_LIFECYCLE_META = {
  deferred: {
    label: 'Отложен',
    stripeClass: 'bg-amber-500',
    badgeClass: 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-900/40'
  },
  cancelled: {
    label: 'Отменён',
    stripeClass: 'bg-red-500',
    badgeClass: 'bg-red-100 text-red-700 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-900/40'
  },
  repeat: {
    label: 'Повтор',
    stripeClass: 'bg-orange-500',
    badgeClass: 'bg-orange-100 text-orange-700 ring-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:ring-orange-900/40'
  },
  rework: {
    label: 'Доработка',
    stripeClass: 'bg-violet-500',
    badgeClass: 'bg-violet-100 text-violet-700 ring-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:ring-violet-900/40'
  },
  completed: {
    label: 'Завершён',
    stripeClass: 'bg-green-500',
    badgeClass: 'bg-green-100 text-green-700 ring-green-200 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-900/40'
  },
  unassigned: {
    label: 'Нераспределён',
    stripeClass: 'bg-gray-400',
    badgeClass: 'bg-gray-100 text-gray-700 ring-gray-300 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700'
  },
  active: {
    label: 'В работе',
    stripeClass: 'bg-blue-500',
    badgeClass: 'bg-blue-100 text-blue-700 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-900/40'
  }
};

const SERVICE_TYPE_OPTIONS = [
  { value: 'all', label: 'Все типы услуг', icon: Package },
  { value: 'washing', label: 'Стирка', icon: Droplets },
  { value: 'cleaning', label: 'Химчистка мебели', icon: Sofa }
];

const CLIENT_TYPE_OPTIONS = [
  { value: 'all', label: 'Все клиенты', icon: User },
  { value: 'individual', label: 'Физическое лицо', icon: User },
  { value: 'company', label: 'Организация', icon: BriefcaseBusiness }
];

const PRIORITY_OPTIONS = [
  { value: 'all', label: 'Любой приоритет', icon: AlertTriangle },
  { value: 'normal', label: 'Обычный', icon: AlertTriangle },
  { value: 'urgent', label: 'Срочный', icon: AlertTriangle },
  { value: 'vip', label: 'VIP', icon: AlertTriangle }
];

const SORT_OPTIONS = [
  { value: 'created_desc', label: 'Сначала новые', icon: Calendar },
  { value: 'created_asc', label: 'Сначала старые', icon: Calendar },
  { value: 'pickup_asc', label: 'По ближайшему забору', icon: Truck },
  { value: 'return_asc', label: 'По дате возврата', icon: Home }
];

const STAGE_FILTER_OPTIONS = [
  { value: 'all', label: 'Все этапы и потоки', icon: Package },
  { value: 'pickup', label: 'Доставка от клиента (Забор)', icon: STAGES_CONFIG.pickup.icon },
  { value: 'washing', label: 'Стирка (все потоки)', icon: STAGES_CONFIG.washing.icon },
  { value: 'washing_cleaning', label: 'Поток · Чистка', icon: STAGES_CONFIG.washing_cleaning.icon },
  { value: 'washing_laundry', label: 'Поток · Прачечная', icon: STAGES_CONFIG.washing_laundry.icon },
  { value: 'drying_packing', label: 'Сушка и упаковка', icon: STAGES_CONFIG.drying_packing.icon },
  { value: 'approval', label: 'Согласование', icon: STAGES_CONFIG.approval.icon },
  { value: 'delivery', label: 'Доставка клиенту', icon: STAGES_CONFIG.delivery.icon },
  { value: 'handover', label: 'Выдача', icon: STAGES_CONFIG.handover.icon },
  { value: 'cleaning', label: 'Химчистка мебели', icon: STAGES_CONFIG.cleaning.icon }
];

const STATUS_FILTER_OPTIONS = [
  { value: 'all', label: 'Все статусы', icon: Package },
  { value: 'active', label: 'В работе', icon: Zap },
  { value: 'unassigned', label: 'Нераспределён', icon: UserMinus },
  { value: 'deferred', label: 'Отложен', icon: Clock },
  { value: 'repeat', label: 'Повтор', icon: RotateCcw },
  { value: 'rework', label: 'Доработка', icon: RotateCcw },
  { value: 'completed', label: 'Завершён', icon: CheckCircle },
  { value: 'cancelled', label: 'Отменён', icon: XCircle }
];

const DATE_TARGET_OPTIONS = [
  { value: 'created', label: 'По созданию', icon: Calendar },
  { value: 'stage_completed', label: 'По этапу', icon: CheckCircle }
];

const PRINT_STAGE_OPTIONS = [
  { value: 'pickup', label: 'Доставка от клиента', icon: STAGES_CONFIG.pickup.icon },
  { value: 'washing', label: 'Стирка · все потоки', icon: STAGES_CONFIG.washing.icon },
  { value: 'washing_cleaning', label: 'Поток · Чистка', icon: STAGES_CONFIG.washing_cleaning.icon },
  { value: 'washing_laundry', label: 'Поток · Прачечная', icon: STAGES_CONFIG.washing_laundry.icon },
  { value: 'drying_packing', label: 'Сушка и упаковка', icon: STAGES_CONFIG.drying_packing.icon },
  { value: 'approval', label: 'Согласование', icon: STAGES_CONFIG.approval.icon },
  { value: 'delivery', label: 'Доставка клиенту', icon: STAGES_CONFIG.delivery.icon },
  { value: 'handover', label: 'Выдача', icon: STAGES_CONFIG.handover.icon },
  { value: 'cleaning', label: 'Химчистка мебели', icon: STAGES_CONFIG.cleaning.icon }
];

const PRINT_ORDER_STATUS_OPTIONS = [
  { value: 'all', label: 'Все статусы', icon: Package, exclusive: true },
  { value: 'unassigned', label: 'Нераспределён', icon: UserMinus },
  { value: 'active', label: 'В работе', icon: Zap },
  { value: 'completed', label: 'Выполнен', icon: CheckCircle },
  { value: 'cancelled', label: 'Отменён', icon: XCircle },
  { value: 'revision_active', label: 'Повторно выполняется', icon: RotateCcw },
  { value: 'deferred', label: 'Отложен', icon: Clock }
];

const ASSEMBLY_SORT_OPTIONS = [
  { value: 'date_time_order_item', label: 'Дата → время → заказ → предмет' },
  { value: 'date_order_item', label: 'Дата → заказ → предмет' },
  { value: 'date_locality_time_order_item', label: 'Дата → населённый пункт → время → заказ' },
  { value: 'order_date_item', label: 'Заказ → дата → предмет' }
];

const PRINT_STATUS_BY_QUICK_VIEW = {
  all: 'all',
  unassigned: 'unassigned',
  active: 'active',
  completed: 'completed',
  cancelled: 'cancelled',
  repeat: 'revision_active',
  deferred: 'deferred'
};

const formatCompactRuDate = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return `${pad2(date.getDate())}.${pad2(date.getMonth() + 1)}.${String(date.getFullYear()).slice(-2)}`;
};

const pad2 = (value) => String(value).padStart(2, '0');

const formatDateInput = (value) => {
  const date = value instanceof Date ? value : new Date(value);
  return `${date.getFullYear()}-${pad2(date.getMonth() + 1)}-${pad2(date.getDate())}`;
};

const parseDateInput = (value) => {
  const normalized = String(value || '').trim();
  if (!normalized) return null;
  const parsed = new Date(`${normalized}T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const addDays = (date, days) => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

const countDaysInclusive = (dateFrom, dateTo) => {
  const from = parseDateInput(dateFrom);
  const to = parseDateInput(dateTo);
  if (!from || !to) return 0;
  return Math.floor((to.getTime() - from.getTime()) / 86400000) + 1;
};

const buildOrderWorkSheetFileName = (stageName, dateFrom, dateTo, compact = false) => {
  const stageLabel = PRINT_STAGE_OPTIONS.find((option) => option.value === stageName)?.label || stageName || 'Этап';
  const safeStage = stageLabel.replace(/[^0-9A-Za-zА-Яа-яЁё_-]+/g, '_').replace(/^_+|_+$/g, '') || 'Этап';
  const normalizedFrom = String(dateFrom || '').split('-').reverse().join('.');
  const normalizedTo = String(dateTo || '').split('-').reverse().join('.');
  const period = normalizedFrom === normalizedTo ? normalizedFrom : `${normalizedFrom}_${normalizedTo}`;
  const layoutSuffix = compact ? '_компактный' : '';
  return `Рабочий_лист_${safeStage}_${period}${layoutSuffix}.pdf`;
};

const buildAssemblySheetFileName = (dateFrom, dateTo) => {
  const normalizedFrom = String(dateFrom || '').split('-').reverse().join('.');
  const normalizedTo = String(dateTo || '').split('-').reverse().join('.');
  const period = normalizedFrom === normalizedTo ? normalizedFrom : `${normalizedFrom}_${normalizedTo}`;
  return `Сборочный_лист_${period}.pdf`;
};

const getDefaultDateRange = () => {
  const today = new Date();
  const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  return {
    date_from: formatDateInput(monthStart),
    date_to: formatDateInput(today),
    datePreset: 'month'
  };
};

const getPresetDateRange = (preset) => {
  const today = new Date();
  switch (preset) {
    case 'today':
      return { date_from: formatDateInput(today), date_to: formatDateInput(today), datePreset: 'today' };
    case 'week':
      return { date_from: formatDateInput(addDays(today, -6)), date_to: formatDateInput(today), datePreset: 'week' };
    case '30d':
      return { date_from: formatDateInput(addDays(today, -29)), date_to: formatDateInput(today), datePreset: '30d' };
    default:
      return getDefaultDateRange();
  }
};

const buildDefaultFilters = (range = getDefaultDateRange()) => ({
  date_from: range.date_from,
  date_to: range.date_to,
  date_mode: 'period',
  date_target: 'created',
  service_type: 'all',
  client_type: 'all',
  department: 'all',
  stage: 'all',
  status: 'all',
  priority: 'all',
  assignee: 'all',
  sort: 'created_desc',
  order_number: '',
  phone: ''
});

const buildExactSearchDraft = (value = {}) => ({
  order_number: String(value?.order_number || ''),
  phone: String(value?.phone || '')
});

const normalizePhoneSearch = (value) => {
  const digits = String(value || '').replace(/\D/g, '');
  if (!digits) return '';
  if (digits.length === 10) return `7${digits}`;
  if (digits.length === 11 && digits[0] === '8') return `7${digits.slice(1)}`;
  return digits;
};

const hasExactSearch = (filters) => (
  !!String(filters?.order_number || '').trim()
  || !!normalizePhoneSearch(filters?.phone)
);

const areFiltersEqual = (left, right) => JSON.stringify(left) === JSON.stringify(right);

const normalizeSavedView = (rawValue) => {
  const fallbackRange = getDefaultDateRange();
  const fallback = {
    filters: buildDefaultFilters(fallbackRange),
    appliedFilters: buildDefaultFilters(fallbackRange),
    exactSearchDraft: buildExactSearchDraft(),
    quickView: 'all',
    searchQuery: '',
    showFilters: false,
    rememberSettings: true,
    datePreset: fallbackRange.datePreset,
    searchMode: 'journal'
  };

  try {
    const parsed = JSON.parse(rawValue || '{}');
    const initialStatus = parsed?.filters?.status || parsed?.quickView || 'all';
    const next = {
      ...fallback,
      ...parsed,
      quickView: initialStatus,
      filters: {
        ...buildDefaultFilters(fallbackRange),
        ...(parsed?.filters || {}),
        status: initialStatus
      },
      appliedFilters: {
        ...buildDefaultFilters(fallbackRange),
        ...(parsed?.appliedFilters || parsed?.filters || {}),
        status: initialStatus
      },
      exactSearchDraft: buildExactSearchDraft(parsed?.exactSearchDraft || parsed?.filters || {}),
      searchMode: parsed?.searchMode || (hasExactSearch(parsed?.exactSearchDraft || parsed?.filters) ? 'exact' : 'journal')
    };
    if (!SORT_OPTIONS.some((option) => option.value === next.filters.sort)) {
      next.filters.sort = fallback.filters.sort;
    }
    if (!SORT_OPTIONS.some((option) => option.value === next.appliedFilters.sort)) {
      next.appliedFilters.sort = fallback.appliedFilters.sort;
    }
    const rangeDays = countDaysInclusive(next.appliedFilters.date_from, next.appliedFilters.date_to);
    if (!rangeDays || rangeDays > MAX_RANGE_DAYS) return fallback;
    return next;
  } catch {
    return fallback;
  }
};

const extractErrorMessage = (payload, fallback = 'Действие не выполнено') => {
  const detail = payload?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (typeof detail === 'object' && detail?.message) return detail.message;
  if (typeof detail === 'object' && Array.isArray(detail?.items)) return detail.items.join('\n');
  if (typeof payload?.message === 'string' && payload.message.trim()) return payload.message;
  return fallback;
};

const requestJson = async (url, options = {}) => {
  const token = localStorage.getItem('token');
  const isFormData = options.body instanceof FormData;
  const response = await fetch(url, {
    ...options,
    headers: {
      ...(options.body && !isFormData ? { 'Content-Type': 'application/json' } : {}),
      Authorization: `Bearer ${token}`,
      ...(options.headers || {})
    }
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(extractErrorMessage(payload));
  }
  return payload;
};

const requestDepartmentsList = async () => {
  const data = await requestJson(`${API_URL}/api/departments`);
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.departments)) return data.departments;
  return [];
};

const buildOrderLogParams = (filters, { offset, quickView, searchQuery, includePage }) => {
  const params = new URLSearchParams();
  const orderNumber = String(filters?.order_number || '').trim();
  const normalizedPhone = normalizePhoneSearch(filters?.phone);
  const isExactSearch = Boolean(orderNumber || normalizedPhone);

  if (includePage) {
    params.set('limit', String(isExactSearch ? 100 : ORDER_LOG_PAGE_SIZE));
    params.set('skip', String(Math.max(0, Number(offset) || 0)));
    params.set('order_log_sort', filters?.sort || 'created_desc');
  }

  if (orderNumber) {
    params.set('order_number', orderNumber);
  } else if (normalizedPhone) {
    params.set('phone', normalizedPhone);
  } else {
    if (filters.stage !== 'deferred') {
      params.set('date_from', filters.date_from);
      params.set('date_to', filters.date_to);
    }
    if (filters.date_target && filters.date_target !== 'created') {
      params.set('date_target', filters.date_target);
    }
    if (filters.service_type && filters.service_type !== 'all') params.set('service_type', filters.service_type);
    if (filters.client_type && filters.client_type !== 'all') params.set('client_type', filters.client_type);
    if (filters.department && filters.department !== 'all') params.set('department_id', filters.department);
    if (filters.priority && filters.priority !== 'all') params.set('priority', filters.priority);
    if (filters.stage && filters.stage !== 'all') params.set('status', filters.stage);
    if (filters.assignee && filters.assignee !== 'all') params.set('order_log_assignee', filters.assignee);
    if (String(searchQuery || '').trim()) params.set('order_log_search', String(searchQuery).trim());
    const effectiveStatus = (quickView && quickView !== 'all') ? quickView : (filters?.status && filters.status !== 'all' ? filters.status : null);
    if (includePage && effectiveStatus) params.set('order_log_quick_view', effectiveStatus);
  }

  return params;
};

const requestOrdersList = async (filters, { offset = 0, quickView = 'all', searchQuery = '' } = {}) => {
  const params = buildOrderLogParams(filters, {
    offset,
    quickView,
    searchQuery,
    includePage: true
  });
  return requestJson(`${API_URL}/api/orders?${params.toString()}`);
};

const requestOrderLogCounts = async (filters, { searchQuery = '' } = {}) => {
  const params = buildOrderLogParams(filters, {
    offset: 0,
    quickView: 'all',
    searchQuery,
    includePage: false
  });
  return requestJson(`${API_URL}/api/orders/order-log-counts?${params.toString()}`);
};

const mergeOrdersById = (currentOrders, nextOrders) => {
  const merged = [...currentOrders];
  const knownIds = new Set(currentOrders.map((order) => order.id));
  nextOrders.forEach((order) => {
    if (!knownIds.has(order.id)) {
      knownIds.add(order.id);
      merged.push(order);
    }
  });
  return merged;
};

const buildOrderLogListKey = (filters, quickView, searchQuery) => JSON.stringify({
  filters,
  quickView,
  searchQuery: String(searchQuery || '').trim()
});

const isCompanyOrder = (order) => ['b2b', 'company', 'organization'].includes(order?.client_type);

const shortenDisplayAddress = (address = '') => {
  const value = String(address || '').trim();
  if (!value) return 'Адрес не указан';
  const parts = value.split(',').map((part) => part.trim()).filter(Boolean);
  const cityIndex = parts.findIndex((part) => /^г\.?\s/i.test(part));
  if (cityIndex > 0 && parts.slice(0, cityIndex).some((part) => /обл\.?|область/i.test(part))) {
    return parts.slice(cityIndex).join(', ');
  }
  return value;
};

const formatTimeSlot = (timeSlot = '') => String(timeSlot || '').trim().replace(/\s*-\s*/g, ' - ');
const formatPhone = (phone) => {
  if (!phone) return '';
  const digits = String(phone).replace(/\D/g, '');
  const normalized = digits.length === 11 && digits[0] === '8' ? `7${digits.slice(1)}` : digits;
  if (normalized.length === 11 && normalized[0] === '7') {
    return `+7(${normalized.slice(1, 4)})-${normalized.slice(4, 7)}-${normalized.slice(7, 9)}-${normalized.slice(9, 11)}`;
  }
  return phone;
};

const formatPhoneInput = (phone) => {
  const digits = String(phone || '').replace(/\D/g, '');
  if (!digits) return '';

  let normalized = digits;
  if (normalized.length >= 1 && normalized[0] === '8') {
    normalized = `7${normalized.slice(1)}`;
  } else if (normalized.length <= 10 && normalized[0] !== '7') {
    normalized = `7${normalized}`;
  }
  normalized = normalized.slice(0, 11);

  const tail = normalized.slice(1);
  if (!tail) return '+7';
  if (tail.length <= 3) return `+7(${tail}`;
  if (tail.length <= 6) return `+7(${tail.slice(0, 3)})-${tail.slice(3)}`;
  if (tail.length <= 8) return `+7(${tail.slice(0, 3)})-${tail.slice(3, 6)}-${tail.slice(6)}`;
  return `+7(${tail.slice(0, 3)})-${tail.slice(3, 6)}-${tail.slice(6, 8)}-${tail.slice(8, 10)}`;
};

const formatAssigneeShortName = (value) => {
  const text = String(value || '').trim();
  if (!text) return 'Не назначен';
  const parts = text.split(/\s+/).filter(Boolean);
  if (parts.length <= 1) return text;
  const [lastName, firstName = '', middleName = ''] = parts;
  const firstInitial = firstName ? `${firstName[0].toUpperCase()}.` : '';
  const middleInitial = middleName ? `${middleName[0].toUpperCase()}.` : '';
  return `${lastName} ${firstInitial}${middleInitial}`.trim();
};

const getOrderStatusMeta = (status) => {
  if (status === 'completed') {
    return { label: 'Завершён', icon: CheckCircle, badgeClass: 'bg-green-100 text-green-700 ring-green-200 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-900/40' };
  }
  if (status === 'cancelled') {
    return { label: 'Отменён', icon: XCircle, badgeClass: 'bg-red-100 text-red-700 ring-red-200 dark:bg-red-900/30 dark:text-red-300 dark:ring-red-900/40' };
  }
  const config = STAGES_CONFIG[status] || {};
  return {
    label: config.label || status || 'Неизвестно',
    icon: config.icon || Package,
    badgeClass: config.color === 'amber'
      ? 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-900/40'
      : config.color === 'orange'
      ? 'bg-orange-100 text-orange-700 ring-orange-200 dark:bg-orange-900/30 dark:text-orange-300 dark:ring-orange-900/40'
      : config.color === 'cyan'
          ? 'bg-cyan-100 text-cyan-700 ring-cyan-200 dark:bg-cyan-900/30 dark:text-cyan-300 dark:ring-cyan-900/40'
          : config.color === 'teal'
            ? 'bg-teal-100 text-teal-700 ring-teal-200 dark:bg-teal-900/30 dark:text-teal-300 dark:ring-teal-900/40'
          : config.color === 'lime'
            ? 'bg-lime-100 text-lime-700 ring-lime-200 dark:bg-lime-900/30 dark:text-lime-300 dark:ring-lime-900/40'
          : config.color === 'rose'
            ? 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-900/30 dark:text-rose-300 dark:ring-rose-900/40'
          : config.color === 'green'
            ? 'bg-green-100 text-green-700 ring-green-200 dark:bg-green-900/30 dark:text-green-300 dark:ring-green-900/40'
          : config.color === 'blue'
            ? 'bg-blue-100 text-blue-700 ring-blue-200 dark:bg-blue-900/30 dark:text-blue-300 dark:ring-blue-900/40'
            : config.color === 'indigo'
              ? 'bg-indigo-100 text-indigo-700 ring-indigo-200 dark:bg-indigo-900/30 dark:text-indigo-300 dark:ring-indigo-900/40'
            : config.color === 'violet'
              ? 'bg-violet-100 text-violet-700 ring-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:ring-violet-900/40'
            : config.color === 'fuchsia'
              ? 'bg-fuchsia-100 text-fuchsia-700 ring-fuchsia-200 dark:bg-fuchsia-900/30 dark:text-fuchsia-300 dark:ring-fuchsia-900/40'
            : config.color === 'purple'
              ? 'bg-violet-100 text-violet-700 ring-violet-200 dark:bg-violet-900/30 dark:text-violet-300 dark:ring-violet-900/40'
              : config.color === 'yellow'
                ? 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-900/30 dark:text-amber-300 dark:ring-amber-900/40'
                : config.color === 'emerald'
                  ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300 dark:ring-emerald-900/40'
                  : 'bg-gray-100 text-gray-700 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700'
  };
};

const getFlowSummaries = (order) => (Array.isArray(order?.washing_flow_summaries) ? order.washing_flow_summaries : []);
const hasAnyAssignedFlow = (order) => getFlowSummaries(order).some((flow) => Number(flow?.assigned_to) > 0);

const getLifecycleKey = (order) => {
  if (order?.status === 'cancelled') return 'cancelled';
  if (order?.is_deferred) return 'deferred';
  if (order?.active_rework) return 'rework';
  if (order?.has_active_revision) return 'repeat';
  if (order?.status === 'completed') return 'completed';
  if (!order?.assigned_to && !hasAnyAssignedFlow(order)) return 'unassigned';
  return 'active';
};

const getLifecycleMeta = (order) => ORDER_LIFECYCLE_META[getLifecycleKey(order)] || ORDER_LIFECYCLE_META.active;

const getRelevantAssigneeEntries = (order, stageFilter = 'all') => {
  const flowSummaries = getFlowSummaries(order);
  if (FLOW_STAGE_NAMES.includes(stageFilter)) {
    const flow = flowSummaries.find((entry) => entry.stage_name === stageFilter);
    if (!flow) return [];
    return flow.assigned_to ? [{
      id: Number(flow.assigned_to),
      name: flow.assignee_name || 'Исполнитель потока',
      stage_name: flow.stage_name
    }] : [];
  }

  if (order?.status === 'drying_packing' && order?.active_rework && flowSummaries.length > 0) {
    const entries = [];
    if (Number(order?.assigned_to) > 0) {
      entries.push({
        id: Number(order.assigned_to),
        name: order.assignee_name || 'Сушка',
        stage_name: 'drying_packing'
      });
    }
    flowSummaries.forEach((flow) => {
      if (Number(flow?.assigned_to) > 0) {
        entries.push({
          id: Number(flow.assigned_to),
          name: flow.assignee_name || 'Исполнитель потока',
          stage_name: flow.stage_name
        });
      }
    });
    return entries;
  }

  if (stageFilter && stageFilter !== 'all') {
    return Number(order?.assigned_to) > 0 ? [{
      id: Number(order.assigned_to),
      name: order.assignee_name || 'Исполнитель этапа',
      stage_name: order.status
    }] : [];
  }

  const entries = [];
  if (Number(order?.assigned_to) > 0) {
    entries.push({
      id: Number(order.assigned_to),
      name: order.assignee_name || 'Исполнитель этапа',
      stage_name: order.status
    });
  }
  flowSummaries.forEach((flow) => {
    if (Number(flow?.assigned_to) > 0) {
      entries.push({
        id: Number(flow.assigned_to),
        name: flow.assignee_name || 'Исполнитель потока',
        stage_name: flow.stage_name
      });
    }
  });

  const unique = new Map();
  entries.forEach((entry) => {
    if (!unique.has(entry.id)) unique.set(entry.id, entry);
  });
  return Array.from(unique.values());
};

const hasSelectedStage = (order, stageFilter, dateTarget = 'created') => {
  if (dateTarget === 'stage_completed') return true;
  if (!stageFilter || stageFilter === 'all') return true;
  if (stageFilter === 'deferred') return !!order?.is_deferred;
  if (FLOW_STAGE_NAMES.includes(stageFilter)) {
    return getFlowSummaries(order).some((flow) => flow.stage_name === stageFilter);
  }
  if (stageFilter === 'washing') {
    return order?.status === 'washing' || (order?.status === 'drying_packing' && !!order?.active_rework && getFlowSummaries(order).length > 0);
  }
  return order?.status === stageFilter;
};

const buildSearchHaystack = (order) => [
  order?.order_number,
  order?.client_name,
  order?.client_phone,
  order?.assignee_name,
  order?.department_name,
  order?.pickup_address_text,
  order?.delivery_address_text,
  ...getFlowSummaries(order).flatMap((flow) => [flow.stage_label, flow.assignee_name])
]
  .filter(Boolean)
  .join(' ')
  .toLowerCase();

const formatIntervalLabel = (dateFrom, dateTo) => {
  const from = parseDateInput(dateFrom);
  const to = parseDateInput(dateTo);
  if (!from || !to) return 'Период не выбран';
  const days = countDaysInclusive(dateFrom, dateTo);
  if (dateFrom === dateTo) {
    return `${formatCompactRuDate(from)} · ${days} день`;
  }
  return `${formatCompactRuDate(from)} - ${formatCompactRuDate(to)} · ${days} дн.`;
};

const getEffectivePickupDate = (order) => order?.has_active_revision
  ? (order?.revision_pickup_date || order?.pickup_date)
  : order?.pickup_date;

const getEffectivePickupTime = (order) => order?.has_active_revision
  ? (order?.revision_pickup_time_slot || order?.pickup_time_slot)
  : order?.pickup_time_slot;

const getEffectivePickupAddress = (order) => order?.has_active_revision
  ? (order?.revision_pickup_address_text || order?.pickup_address_text)
  : order?.pickup_address_text;

const getEffectiveDeliveryDate = (order) => (
  order?.has_active_revision && order?.revision_return_delivery_type === 'delivery'
) ? order?.revision_return_date : order?.delivery_date;

const getEffectiveDeliveryTime = (order) => (
  order?.has_active_revision && order?.revision_return_delivery_type === 'delivery'
) ? order?.revision_return_time_slot : order?.delivery_time_slot;

const getEffectiveDeliveryAddress = (order) => (
  order?.has_active_revision && order?.revision_return_delivery_type === 'delivery'
) ? (order?.revision_return_address_text || order?.delivery_address_text) : order?.delivery_address_text;

const getJournalCardStage = (order, stageFilter) => {
  if (FLOW_STAGE_NAMES.includes(stageFilter)) return stageFilter;
  if (stageFilter === 'deferred') return order?.status || 'pickup';
  if (stageFilter && stageFilter !== 'all') return stageFilter;
  return order?.status || 'pickup';
};

const getJournalCardAddress = (order, stageName) => {
  const isReturnStage = stageName === 'delivery' || stageName === 'handover';
  if (isReturnStage) {
    const returnType = order?.has_active_revision
      ? (order?.revision_return_delivery_type || order?.return_delivery_type)
      : order?.return_delivery_type;
    if (returnType === 'self_pickup') return 'Выдача в точке';
    return shortenDisplayAddress(getEffectiveDeliveryAddress(order));
  }
  return shortenDisplayAddress(getEffectivePickupAddress(order));
};

const getJournalCardTopMeta = (order, stageName, dateTarget = 'created') => {
  if (dateTarget === 'stage_completed' && order?.stage_completed_date) {
    const stageLabel = order.stage_completed_name || STAGES_CONFIG[stageName]?.label || 'Этап';
    const currentStatusMeta = getOrderStatusMeta(order.status);
    return {
      primary: `${stageLabel}: ${formatTimezoneDate(order.stage_completed_date, true)}`,
      secondary: currentStatusMeta?.label || order.status || 'В работе',
      primaryIcon: CheckCircle,
      secondaryIcon: currentStatusMeta?.icon || Clock
    };
  }

  const createdDateText = formatTimezoneDate(order?.created_date, false);
  const pickupDateText = getEffectivePickupDate(order) ? formatTimezoneDate(getEffectivePickupDate(order), false) : 'Дата не указана';
  const pickupTimeText = formatTimeSlot(getEffectivePickupTime(order)) || 'Время не указано';
  const deliveryDateText = getEffectiveDeliveryDate(order) ? formatTimezoneDate(getEffectiveDeliveryDate(order), false) : 'Дата не указана';
  const deliveryTimeText = formatTimeSlot(getEffectiveDeliveryTime(order)) || 'Время не указано';

  if (stageName === 'delivery') {
    return {
      primary: deliveryDateText,
      secondary: deliveryTimeText,
      primaryIcon: Truck,
      secondaryIcon: Clock
    };
  }

  if (stageName === 'handover') {
    return {
      primary: deliveryDateText !== 'Дата не указана' ? deliveryDateText : createdDateText,
      secondary: 'Самовывоз',
      primaryIcon: Home,
      secondaryIcon: Clock
    };
  }

  if (stageName === 'cleaning') {
    return {
      primary: pickupDateText,
      secondary: pickupTimeText,
      primaryIcon: Truck,
      secondaryIcon: Clock
    };
  }

  if (['washing', 'washing_laundry', 'washing_cleaning', 'drying_packing', 'approval'].includes(stageName)) {
    return {
      primary: createdDateText,
      secondary: order?.self_delivery ? 'Самопривоз' : pickupDateText,
      primaryIcon: Calendar,
      secondaryIcon: Truck
    };
  }

  if (stageName === 'completed') {
    return {
      primary: order?.completed_date ? formatTimezoneDate(order.completed_date, false) : createdDateText,
      secondary: 'Завершён',
      primaryIcon: CheckCircle,
      secondaryIcon: Package
    };
  }

  if (stageName === 'cancelled') {
    return {
      primary: createdDateText,
      secondary: 'Отменён',
      primaryIcon: XCircle,
      secondaryIcon: Package
    };
  }

  return {
    primary: order?.self_delivery ? 'Самопривоз' : pickupDateText,
    secondary: order?.self_delivery ? '' : pickupTimeText,
    primaryIcon: Truck,
    secondaryIcon: Clock
  };
};

const getJournalStageChips = (order, stageName, itemsCount, completedCount) => {
  const chips = [];
  const pushChip = (key, icon, text, iconClassName) => {
    chips.push({ key, icon, text, iconClassName });
  };

  const isWashingStage = ['washing', 'washing_laundry', 'washing_cleaning'].includes(stageName);

  if (isWashingStage || stageName === 'drying_packing' || stageName === 'cleaning') {
    pushChip('ready', Check, `${completedCount}/${itemsCount || 0}`, 'text-green-600 dark:text-green-400');
  }

  if (order?.active_rework) {
    const reworkCount = Number(order.rework_items_count || 0);
    pushChip('rework', RotateCcw, reworkCount > 0 ? `Доработка: ${reworkCount}` : 'Доработка', 'text-amber-600 dark:text-amber-400');
  }

  if (stageName === 'pickup' || stageName === 'delivery' || stageName === 'handover' || stageName === 'approval' || stageName === 'cleaning') {
    pushChip('items', stageName === 'pickup' ? Package : ShoppingCart, `${itemsCount}`, 'text-blue-600 dark:text-blue-400');
  }

  if (isWashingStage) {
    if (Number(order.area_required_count || 0) > 0) {
      pushChip('area', Ruler, `${order.area_filled_count}/${order.area_required_count}`, 'text-violet-600 dark:text-violet-400');
    }
    if (Number(order.weight_required_count || 0) > 0) {
      pushChip('weight', Scale, `${order.weight_filled_count}/${order.weight_required_count}`, 'text-amber-600 dark:text-amber-400');
    }
  }

  return chips;
};

const getSortTimestamp = (value) => {
  if (!value) return Number.MAX_SAFE_INTEGER;
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? Number.MAX_SAFE_INTEGER : parsed.getTime();
};

const getAssigneeFilterOptions = (orders, stageFilter) => {
  let hasUnassigned = false;

  orders.forEach((order) => {
    const entries = getRelevantAssigneeEntries(order, stageFilter);
    if (entries.length === 0) hasUnassigned = true;
  });

  return [
    { value: 'all', label: 'Любой исполнитель', icon: UserCheck },
    { value: 'unassigned', label: hasUnassigned ? 'Без исполнителя' : 'Без исполнителя', icon: AlertTriangle }
  ];
};

const OrderLogLoadingSkeleton = React.memo(function OrderLogLoadingSkeleton({ count = 4 }) {
  return (
    <div className="space-y-4">
      {Array.from({ length: count }, (_, index) => (
        <div key={index} className="overflow-hidden rounded-xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-900">
          <div className="animate-pulse">
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2">
                <div className="h-6 w-16 rounded-md bg-gray-200 dark:bg-gray-700" />
                <div className="h-6 w-6 rounded-md bg-gray-200 dark:bg-gray-700" />
              </div>
              <div className="h-4 w-24 rounded bg-gray-200 dark:bg-gray-700" />
            </div>
            <div className="mt-3 h-px w-full bg-gray-200 dark:bg-gray-700" />
            <div className="mt-3 flex items-start justify-between gap-3">
              <div className="min-w-0 flex-1 space-y-2">
                <div className="h-3.5 w-32 rounded bg-gray-200 dark:bg-gray-700" />
                <div className="h-4 w-40 rounded bg-gray-200 dark:bg-gray-700" />
              </div>
              <div className="h-9 w-9 rounded-lg bg-gray-200 dark:bg-gray-700" />
            </div>
            <div className="mt-2 h-4 w-11/12 rounded bg-gray-200 dark:bg-gray-700" />
            <div className="mt-3 flex gap-2">
              <div className="h-6 w-14 rounded-md bg-gray-200 dark:bg-gray-700" />
              <div className="h-6 w-12 rounded-md bg-gray-200 dark:bg-gray-700" />
              <div className="h-6 w-10 rounded-md bg-gray-200 dark:bg-gray-700" />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
});

const JournalOrderCard = React.memo(function JournalOrderCard({
  order,
  appliedStageFilter,
  isAppliedExactSearchMode,
  dateTarget = 'created',
  onOpenOrder,
  onOpenAssignModal
}) {
  const statusMeta = getOrderStatusMeta(order.status);
  const lifecycleMeta = getLifecycleMeta(order);
  const StatusIcon = statusMeta.icon || Package;
  const ClientIcon = isCompanyOrder(order) ? BriefcaseBusiness : User;
  const cardStage = getJournalCardStage(order, isAppliedExactSearchMode ? 'all' : appliedStageFilter);
  const flowSummaries = getFlowSummaries(order);
  const itemsCount = Number(order.items_count || 0);
  const completedCount = Number(order.stage_completed_items_count || 0);
  const topMeta = getJournalCardTopMeta(order, cardStage, dateTarget);
  const TopMetaPrimaryIcon = topMeta.primaryIcon || Calendar;
  const TopMetaSecondaryIcon = topMeta.secondaryIcon || Clock;
  const stageAddress = getJournalCardAddress(order, cardStage);
  const stageChips = getJournalStageChips(order, cardStage, itemsCount, completedCount);
  const relevantFlowSummaries = FLOW_STAGE_NAMES.includes(cardStage)
    ? flowSummaries.filter((flow) => flow.stage_name === cardStage)
    : (cardStage === 'washing' ? flowSummaries : []);
  const showFlowAssignees = relevantFlowSummaries.length > 0;
  const isReworkDryingOrder = order?.status === 'drying_packing' && Boolean(order?.active_rework) && flowSummaries.length > 0 && !FLOW_STAGE_NAMES.includes(cardStage);
  const activeReworkFlows = isReworkDryingOrder
    ? (flowSummaries.filter((flow) => flow.stage_status === 'pending' || flow.stage_status === 'in_progress').length > 0
        ? flowSummaries.filter((flow) => flow.stage_status === 'pending' || flow.stage_status === 'in_progress')
        : flowSummaries)
    : [];
  const hasDryerAssignee = !!String(order?.assignee_name || '').trim();
  const assigneeEntries = (showFlowAssignees || isReworkDryingOrder) ? [] : getRelevantAssigneeEntries(order, cardStage);
  const isArchivedLifecycle = ['completed', 'cancelled'].includes(order.status);
  const hasAnyVisibleAssignee = isReworkDryingOrder
    ? (hasDryerAssignee || activeReworkFlows.some((flow) => !!String(flow.assignee_name || '').trim()))
    : showFlowAssignees
    ? relevantFlowSummaries.some((flow) => !!String(flow.assignee_name || '').trim())
    : assigneeEntries.length > 0;
  const clientDisplayName = String(order.client_name || '').trim() || (isCompanyOrder(order) ? 'Юр. лицо' : 'Физ. лицо');
  const formattedPhone = formatPhone(order.client_phone);

  return (
    <article
      role="button"
      tabIndex={0}
      onClick={() => onOpenOrder(order)}
      onKeyDown={(event) => {
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onOpenOrder(order);
        }
      }}
      className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition hover:border-gray-300 active:scale-[0.99] dark:border-gray-700 dark:bg-gray-900 dark:hover:border-gray-600"
    >
      <div className="px-4 pb-3 pt-2">
        <div className="grid grid-cols-[minmax(0,1fr)_auto] gap-x-3 gap-y-1">
          <div className="flex min-w-0 items-center gap-2">
            <span className={`inline-flex h-6 items-center self-center rounded-md px-1.5 text-[11px] font-medium leading-none ring-1 ${lifecycleMeta.badgeClass}`}>
              № {order.order_number}
            </span>
            <span
              className={`inline-flex h-6 w-6 items-center justify-center self-center rounded-md ring-1 ${statusMeta.badgeClass}`}
              title={statusMeta.label}
              aria-label={statusMeta.label}
            >
              <StatusIcon className="h-3 w-3 shrink-0" />
            </span>
          </div>
          <div className="flex h-6 items-center justify-end gap-1.5 text-right text-[12px] font-medium leading-4 text-gray-900 dark:text-gray-100">
            <TopMetaPrimaryIcon className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
            <span>{topMeta.primary}</span>
          </div>
          <div className="flex min-h-4 min-w-0 items-center gap-1.5 text-[11px] leading-4 text-gray-600 dark:text-gray-300">
            <Building2 className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
            <span className="min-w-0 truncate">{order.department_name || 'Подразделение не выбрано'}</span>
          </div>
          <div className="flex min-h-4 items-center justify-end gap-1.5 text-right text-[11px] font-medium leading-4 text-gray-500 dark:text-gray-400">
            <TopMetaSecondaryIcon className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
            <span>{topMeta.secondary}</span>
          </div>
          <div className="flex min-h-5 min-w-0 flex-wrap items-center gap-2 text-[11px] leading-4 text-gray-600 dark:text-gray-300">
            {isReworkDryingOrder ? (
              <>
                <span
                  key={`${order.id}-drying-assignee`}
                  className={`inline-flex min-w-0 items-center gap-1 font-medium ${
                    hasDryerAssignee ? 'text-gray-700 dark:text-gray-300' : 'text-orange-800 dark:text-orange-200'
                  }`}
                  title={hasDryerAssignee ? `Сушка и упаковка: ${order.assignee_name}` : 'Сушка: не назначен'}
                >
                  <Package
                    className={`h-3 w-3 shrink-0 ${
                      hasDryerAssignee ? 'text-purple-600 dark:text-purple-400' : 'text-orange-700 dark:text-orange-300'
                    }`}
                  />
                  <span className="truncate max-w-[140px]">
                    {hasDryerAssignee ? `Сушка: ${formatAssigneeShortName(order.assignee_name)}` : 'Сушка: не назначен'}
                  </span>
                </span>
                {activeReworkFlows.map((flow) => {
                  const flowConfig = STAGES_CONFIG[flow.stage_name] || {};
                  const FlowIcon = flowConfig.icon || UserCheck;
                  const hasFlowAssignee = !!String(flow.assignee_name || '').trim();
                  const flowLabel = flow.stage_label || (flow.stage_name === 'washing_cleaning' ? 'Чистка' : 'Стирка');
                  const flowAssigneeText = hasFlowAssignee ? formatAssigneeShortName(flow.assignee_name) : 'Не назначен';

                  return (
                    <span
                      key={`${order.id}-${flow.stage_name}`}
                      className={`inline-flex min-w-0 items-center gap-1 font-medium ${
                        hasFlowAssignee ? 'text-amber-800 dark:text-amber-300' : 'text-orange-800 dark:text-orange-200'
                      }`}
                      title={`${flowLabel} (доработка): ${flowAssigneeText}`}
                    >
                      <span className="relative flex shrink-0 items-center">
                        <FlowIcon
                          className={`h-3 w-3 ${
                            hasFlowAssignee ? 'text-amber-600 dark:text-amber-400' : 'text-orange-700 dark:text-orange-300'
                          }`}
                        />
                        <RotateCcw
                          className={`absolute -bottom-0.5 -right-1 h-2 w-2 ${
                            hasFlowAssignee ? 'text-amber-600 dark:text-amber-400' : 'text-orange-700 dark:text-orange-300'
                          }`}
                        />
                      </span>
                      <span className="truncate max-w-[140px]">
                        {flowLabel}: {flowAssigneeText}
                      </span>
                    </span>
                  );
                })}
              </>
            ) : showFlowAssignees ? (
              relevantFlowSummaries.map((flow) => {
                const flowConfig = STAGES_CONFIG[flow.stage_name] || {};
                const FlowIcon = flowConfig.icon || UserCheck;
                const hasAssignee = !!String(flow.assignee_name || '').trim();
                const isReworkFlow = Boolean(order?.active_rework);
                return (
                  <span
                    key={`${order.id}-${flow.stage_name}`}
                    className={`inline-flex min-w-0 items-center gap-1 font-medium ${
                      hasAssignee
                        ? (isReworkFlow ? 'text-amber-800 dark:text-amber-300' : 'text-gray-700 dark:text-gray-300')
                        : 'text-orange-800 dark:text-orange-200'
                    }`}
                  >
                    <span className="relative flex shrink-0 items-center">
                      <FlowIcon
                        className={`h-3 w-3 ${
                          hasAssignee
                            ? (isReworkFlow ? 'text-amber-600 dark:text-amber-400' : 'text-gray-500 dark:text-gray-400')
                            : 'text-orange-700 dark:text-orange-300'
                        }`}
                      />
                      {isReworkFlow && (
                        <RotateCcw
                          className={`absolute -bottom-0.5 -right-1 h-2 w-2 ${
                            hasAssignee ? 'text-amber-600 dark:text-amber-400' : 'text-orange-700 dark:text-orange-300'
                          }`}
                        />
                      )}
                    </span>
                    <span className="truncate max-w-[140px]">
                      {hasAssignee ? formatAssigneeShortName(flow.assignee_name) : 'Не назначен'}
                    </span>
                  </span>
                );
              })
            ) : assigneeEntries.length > 0 ? (
              assigneeEntries.map((entry) => (
                <span key={`${order.id}-${entry.id}-${entry.stage_name}`} className="inline-flex min-w-0 items-center gap-1 font-medium text-gray-700 dark:text-gray-300">
                  <UserCheck className="h-3 w-3 shrink-0 text-gray-500 dark:text-gray-400" />
                  <span className="truncate max-w-[160px]">{formatAssigneeShortName(entry.name)}</span>
                </span>
              ))
            ) : !isArchivedLifecycle ? (
              <span className="inline-flex items-center gap-1 font-medium text-orange-800 dark:text-orange-200">
                <UserCheck className="h-3 w-3 shrink-0 text-orange-700 dark:text-orange-300" />
                <span>Не назначен</span>
              </span>
            ) : null}
          </div>
          <div className="flex min-h-5 items-center justify-end gap-2">
            {order.has_unread_comments && (
              <span className="inline-flex h-6 min-w-6 items-center justify-center gap-1 rounded-lg bg-gray-100 px-1.5 text-[11px] font-semibold text-gray-700 ring-1 ring-gray-200 dark:bg-gray-800 dark:text-gray-300 dark:ring-gray-700">
                <MessageCircle className="h-3 w-3 text-red-500 dark:text-red-400" />
                {order.unread_comments_count || ''}
              </span>
            )}
            {order.client_vip && (
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg border border-violet-700/70 bg-violet-50 text-violet-800 dark:border-violet-500/70 dark:bg-violet-950/30 dark:text-violet-200" title="VIP клиент">
                <Star className="h-3 w-3 fill-current text-violet-700 dark:text-violet-300" />
              </span>
            )}
            {order.priority === 'urgent' && (
              <span className="inline-flex h-6 w-6 items-center justify-center rounded-lg border border-orange-700/70 bg-orange-50 text-orange-800 dark:border-orange-500/70 dark:bg-orange-950/30 dark:text-orange-200" title="Срочный заказ">
                <Zap className="h-3 w-3 text-orange-700 dark:text-orange-300" />
              </span>
            )}
          </div>
        </div>

        <div className="mt-1.5 border-t border-gray-200 dark:border-gray-700" />

        {order.is_deferred && order.deferred_event && (
          <div className="mt-2 rounded-lg border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-900 dark:border-amber-900/60 dark:bg-amber-950/25 dark:text-amber-100">
            <div className="font-semibold">{order.deferred_event.reason_label || 'Отложено'}</div>
            <div className="mt-0.5 line-clamp-2 leading-4 text-amber-800 dark:text-amber-200">{order.deferred_event.comment}</div>
            <div className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">
              {formatTimezoneDate(order.deferred_event.deferred_at, true)} · было у {order.deferred_event.previous_assignee_name || 'не указано'}
            </div>
          </div>
        )}

        <div className="mt-2 flex min-h-6 items-start justify-between gap-3">
          <div className="min-w-0 flex-1">
            <div className="flex min-w-0 items-center gap-1.5 text-[11px] leading-4 text-gray-600 dark:text-gray-300">
              <ClientIcon className="h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
              <span className="min-w-0 truncate">{clientDisplayName}</span>
            </div>

            <div className="mt-0.5 flex flex-wrap items-center gap-1.5 text-[12px] leading-4 text-gray-700 dark:text-gray-300">
              <Phone className="h-3.5 w-3.5 shrink-0 text-gray-400 dark:text-gray-500" />
              <span>{formattedPhone || 'Телефон не указан'}</span>
            </div>
          </div>

          {!order.is_deferred && (!isArchivedLifecycle || hasAnyVisibleAssignee) && (
            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                haptic('tap');
                onOpenAssignModal(order);
              }}
              className="route-order-action-button inline-flex h-9 w-9 min-h-0 min-w-0 shrink-0 self-center items-center justify-center rounded-lg border border-blue-300 bg-blue-50 p-0 leading-none text-blue-700 transition-colors hover:border-blue-400 hover:bg-blue-100 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300 dark:hover:border-blue-700 dark:hover:bg-blue-950/50"
              title="Назначить исполнителя"
              aria-label="Назначить исполнителя"
            >
              <UserCog className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="mt-0.5 flex items-start gap-1 text-[12px] font-medium leading-4 text-gray-950 dark:text-gray-50">
          <MapPin className="mt-0.5 h-3 w-3 shrink-0 text-gray-400 dark:text-gray-500" />
          <span className="break-words">{stageAddress}</span>
        </div>

        <div className="mt-2 flex flex-wrap items-center gap-2 text-xs font-medium leading-4 text-gray-700 dark:text-gray-300">
          {stageChips.map((chip) => {
            const ChipIcon = chip.icon;
            return (
              <span key={`${order.id}-${chip.key}`} className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-1.5 py-1 text-[11px] font-medium text-gray-700 dark:bg-gray-800 dark:text-gray-300">
                <ChipIcon className={`h-3 w-3 shrink-0 ${chip.iconClassName}`} />
                <span>{chip.text}</span>
              </span>
            );
          })}
        </div>
      </div>
    </article>
  );
});

const OrderLogScreen = () => {
  const navigate = useNavigate();
  const isDryCleaningEnabled = useDryCleaningEnabled();
  const initialView = useMemo(() => normalizeSavedView(localStorage.getItem(STORAGE_KEY)), []);

  const visiblePrintStageOptions = useMemo(() => (
    PRINT_STAGE_OPTIONS.filter((option) => isDryCleaningEnabled || option.value !== 'cleaning')
  ), [isDryCleaningEnabled]);

  const visibleStageFilterOptions = useMemo(() => (
    STAGE_FILTER_OPTIONS.filter((option) => isDryCleaningEnabled || option.value !== 'cleaning')
  ), [isDryCleaningEnabled]);

  const visibleServiceTypeOptions = useMemo(() => (
    SERVICE_TYPE_OPTIONS.filter((option) => isDryCleaningEnabled || option.value !== 'cleaning')
  ), [isDryCleaningEnabled]);

  const [orders, setOrders] = useState([]);
  const [orderCounts, setOrderCounts] = useState(EMPTY_ORDER_LOG_COUNTS);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(initialView.showFilters);
  const [showTools, setShowTools] = useState(false);
  const toolsRef = useRef(null);
  const desktopToolsRef = useRef(null);
  const [showQuickFilters, setShowQuickFilters] = useState(true);
  const [rememberSettings, setRememberSettings] = useState(initialView.rememberSettings);
  const [datePreset, setDatePreset] = useState(initialView.datePreset);
  const [quickView, setQuickView] = useState(initialView.quickView);
  const [searchQuery, setSearchQuery] = useState(initialView.searchQuery);
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState(initialView.searchQuery);
  const [filters, setFilters] = useState(initialView.filters);
  const [appliedFilters, setAppliedFilters] = useState(initialView.appliedFilters || initialView.filters);
  const [exactSearchDraft, setExactSearchDraft] = useState(buildExactSearchDraft(initialView.appliedFilters || initialView.filters));
  const [searchMode, setSearchMode] = useState(() => (
    hasExactSearch(initialView.appliedFilters || initialView.filters || initialView.exactSearchDraft)
      ? 'exact'
      : (initialView.searchMode || 'journal')
  ));
  const [isMobileFiltersOpen, setIsMobileFiltersOpen] = useState(false);
  const { showToast } = useToast();
  const [assignModalOrder, setAssignModalOrder] = useState(null);
  const [assigneeDraft, setAssigneeDraft] = useState(UNASSIGN_ASSIGNEE_VALUE);
  const [assigneeStageDraft, setAssigneeStageDraft] = useState('');
  const [availableAssignees, setAvailableAssignees] = useState([]);
  const [availableAssigneeFlows, setAvailableAssigneeFlows] = useState([]);
  const [assigneesLoading, setAssigneesLoading] = useState(false);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [bulkModalOpen, setBulkModalOpen] = useState(false);
  const [bulkStages, setBulkStages] = useState([]);
  const [bulkStageDraft, setBulkStageDraft] = useState('');
  const [bulkAssigneeDraft, setBulkAssigneeDraft] = useState('');
  const [bulkOptionsLoading, setBulkOptionsLoading] = useState(false);
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [printModalOpen, setPrintModalOpen] = useState(false);
  const [printDocumentTypeDraft, setPrintDocumentTypeDraft] = useState('work');
  const [printStageDraft, setPrintStageDraft] = useState('washing');
  const [printOrderStatusesDraft, setPrintOrderStatusesDraft] = useState(['all']);
  const [printRangeMode, setPrintRangeMode] = useState('date');
  const [printAssemblySortDraft, setPrintAssemblySortDraft] = useState('date_time_order_item');
  const [printCompactDraft, setPrintCompactDraft] = useState(false);
  const [printDateFrom, setPrintDateFrom] = useState(() => formatDateInput(new Date()));
  const [printDateTo, setPrintDateTo] = useState(() => formatDateInput(new Date()));
  const [printLoading, setPrintLoading] = useState(false);
  const [printObjectUrl, setPrintObjectUrl] = useState('');
  const [printFileName, setPrintFileName] = useState('');
  const [printTaskCount, setPrintTaskCount] = useState(null);

  useEffect(() => {
    if (!isDryCleaningEnabled) {
      if (printStageDraft === 'cleaning') {
        setPrintStageDraft('washing');
      }
      if (filters.stage === 'cleaning') {
        setFilters((prev) => ({ ...prev, stage: 'all' }));
      }
      if (appliedFilters.stage === 'cleaning') {
        setAppliedFilters((prev) => ({ ...prev, stage: 'all' }));
      }
      if (filters.service_type === 'cleaning') {
        setFilters((prev) => ({ ...prev, service_type: 'all' }));
      }
      if (appliedFilters.service_type === 'cleaning') {
        setAppliedFilters((prev) => ({ ...prev, service_type: 'all' }));
      }
    }
  }, [
    isDryCleaningEnabled,
    printStageDraft,
    filters.stage,
    appliedFilters.stage,
    filters.service_type,
    appliedFilters.service_type
  ]);
  const loadMoreSentinelRef = useRef(null);
  const activeListKeyRef = useRef('');
  const activeCountsKeyRef = useRef('');
  const listRequestSequenceRef = useRef(0);
  const countsRequestSequenceRef = useRef(0);
  const loadMoreInFlightRef = useRef(false);

  useModalBackButton(!!assignModalOrder, () => {
    if (!assignmentSaving) {
      setAssignModalOrder(null);
      setAvailableAssignees([]);
      setAvailableAssigneeFlows([]);
      setAssigneeStageDraft('');
      setAssigneeDraft(UNASSIGN_ASSIGNEE_VALUE);
    }
  });

  const closeBulkModal = useCallback(() => {
    setBulkModalOpen(false);
    setBulkConfirmOpen(false);
    setBulkStages([]);
    setBulkStageDraft('');
    setBulkAssigneeDraft('');
  }, []);

  useModalBackButton(bulkModalOpen, () => {
    if (!bulkSaving) closeBulkModal();
  });

  useModalBackButton(bulkConfirmOpen, () => {
    if (!bulkSaving) setBulkConfirmOpen(false);
  });

  const resetPreparedPrint = useCallback(() => {
    setPrintObjectUrl('');
    setPrintFileName('');
    setPrintTaskCount(null);
  }, []);

  const closePrintModal = useCallback(() => {
    setPrintModalOpen(false);
    resetPreparedPrint();
  }, [resetPreparedPrint]);

  useModalBackButton(printModalOpen, () => {
    if (!printLoading) closePrintModal();
  });

  useModalBackButton(isMobileFiltersOpen, () => {
    setIsMobileFiltersOpen(false);
  });

  useEffect(() => () => {
    if (printObjectUrl) URL.revokeObjectURL(printObjectUrl);
  }, [printObjectUrl]);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setDebouncedSearchQuery(searchQuery);
    }, 250);
    return () => window.clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    if (!rememberSettings) {
      localStorage.removeItem(STORAGE_KEY);
      return;
    }
    localStorage.setItem(STORAGE_KEY, JSON.stringify({
      filters,
      appliedFilters,
      quickView,
      searchQuery,
      showFilters,
      rememberSettings,
      datePreset,
      searchMode,
      exactSearchDraft
    }));
  }, [filters, appliedFilters, quickView, searchQuery, showFilters, rememberSettings, datePreset, searchMode, exactSearchDraft]);

  const reloadOrders = async ({ silent = false, nextFilters = appliedFilters } = {}) => {
    const exactSearchMode = hasExactSearch(nextFilters);
    const resolvedQuickView = exactSearchMode ? 'all' : quickView;
    const resolvedSearchQuery = exactSearchMode ? '' : debouncedSearchQuery;
    const listKey = buildOrderLogListKey(nextFilters, resolvedQuickView, resolvedSearchQuery);
    const countsKey = buildOrderLogParams(nextFilters, {
      offset: 0,
      quickView: 'all',
      searchQuery: resolvedSearchQuery,
      includePage: false
    }).toString();
    const shouldReloadCounts = silent || activeCountsKeyRef.current !== countsKey;
    const listRequestId = ++listRequestSequenceRef.current;
    const countsRequestId = shouldReloadCounts ? ++countsRequestSequenceRef.current : countsRequestSequenceRef.current;
    activeListKeyRef.current = listKey;
    if (shouldReloadCounts) activeCountsKeyRef.current = countsKey;
    loadMoreInFlightRef.current = false;
    setLoading(true);
    setLoadingMore(false);
    setHasMore(false);
    if (!silent) {
      setOrders([]);
      if (shouldReloadCounts) setOrderCounts(EMPTY_ORDER_LOG_COUNTS);
    }

    const countsTask = shouldReloadCounts ? requestOrderLogCounts(nextFilters, { searchQuery: resolvedSearchQuery })
      .then((data) => {
        if (countsRequestSequenceRef.current !== countsRequestId) return;
        setOrderCounts({
          ...EMPTY_ORDER_LOG_COUNTS,
          ...(data && typeof data === 'object' ? data : {})
        });
      })
      .catch(() => {
        if (countsRequestSequenceRef.current === countsRequestId) {
          activeCountsKeyRef.current = '';
          if (!silent) setOrderCounts(EMPTY_ORDER_LOG_COUNTS);
        }
      }) : Promise.resolve();

    const ordersTask = requestOrdersList(nextFilters, {
      offset: 0,
      quickView: resolvedQuickView,
      searchQuery: resolvedSearchQuery
    })
      .then((data) => {
        if (listRequestSequenceRef.current !== listRequestId) return;
        const nextOrders = Array.isArray(data) ? data : [];
        setOrders(nextOrders);
        setHasMore(nextOrders.length === ORDER_LOG_PAGE_SIZE);
      })
      .catch((error) => {
        if (listRequestSequenceRef.current !== listRequestId) return;
        setOrders([]);
        if (!silent) showToast('error', error.message);
      })
      .finally(() => {
        if (listRequestSequenceRef.current === listRequestId) {
          setLoading(false);
        }
      });

    await Promise.allSettled([countsTask, ordersTask]);
  };

  useEffect(() => {
    reloadOrders({ silent: false, nextFilters: appliedFilters });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appliedFilters, quickView, debouncedSearchQuery]);

  useEffect(() => {
    let active = true;
    const loadDepartments = async () => {
      try {
        const data = await requestDepartmentsList();
        if (!active) return;
        setDepartments(Array.isArray(data) ? data : []);
      } catch {
        if (!active) return;
        setDepartments([]);
      }
    };
    loadDepartments();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!showTools) return;
    const handleOutside = (e) => {
      const inMobile = toolsRef.current && toolsRef.current.contains(e.target);
      const inDesktop = desktopToolsRef.current && desktopToolsRef.current.contains(e.target);
      if (!inMobile && !inDesktop) {
        setShowTools(false);
      }
    };
    document.addEventListener('mousedown', handleOutside);
    document.addEventListener('touchstart', handleOutside);
    return () => {
      document.removeEventListener('mousedown', handleOutside);
      document.removeEventListener('touchstart', handleOutside);
    };
  }, [showTools]);

  const updateDateFilter = (field, value) => {
    setFilters((prev) => {
      const next = { ...prev, [field]: value };
      let nextFrom = next.date_from;
      let nextTo = next.date_to;

      if (nextFrom && nextTo) {
        const fromDate = parseDateInput(nextFrom);
        const toDate = parseDateInput(nextTo);
        if (!fromDate || !toDate) return prev;
        if (fromDate > toDate) {
          if (field === 'date_from') nextTo = nextFrom;
          else nextFrom = nextTo;
        }

        const days = countDaysInclusive(nextFrom, nextTo);
        if (days > MAX_RANGE_DAYS) {
          showToast('warning', `Журнал можно открыть максимум за ${MAX_RANGE_DAYS} дней`);
          return prev;
        }
      }

      setDatePreset('custom');
      return { ...next, date_from: nextFrom, date_to: nextTo };
    });
  };

  const applyDatePreset = (preset) => {
    if (searchMode === 'exact') return;
    const range = getPresetDateRange(preset);
    setFilters((prev) => ({
      ...prev,
      date_from: range.date_from,
      date_to: range.date_to
    }));
    setDatePreset(range.datePreset);
  };

  const resetAllFilters = () => {
    haptic('tap');
    const range = getDefaultDateRange();
    setFilters(buildDefaultFilters(range));
    setDatePreset(range.datePreset);
  };

  const resetAndApplyAllFilters = () => {
    haptic('tap');
    const range = getDefaultDateRange();
    const nextFilters = buildDefaultFilters(range);
    setFilters(nextFilters);
    setAppliedFilters(nextFilters);
    setExactSearchDraft(buildExactSearchDraft());
    setSearchMode('journal');
    setDatePreset(range.datePreset);
    setQuickView('all');
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setShowFilters(false);
    setIsMobileFiltersOpen(false);
  };

  const handleSwitchMode = (nextMode) => {
    if (nextMode === searchMode) return;
    haptic('tap');
    setSearchMode(nextMode);
    if (nextMode === 'journal') {
      setExactSearchDraft(buildExactSearchDraft());
      if (isAppliedExactSearchMode) {
        const cleanFilters = {
          ...filters,
          order_number: '',
          phone: ''
        };
        setAppliedFilters(cleanFilters);
        setQuickView('all');
      }
    }
  };

  const applyExactSearch = () => {
    const cleanNumber = String(exactSearchDraft.order_number || '').trim();
    const cleanPhone = String(exactSearchDraft.phone || '').trim();
    if (!cleanNumber && !cleanPhone) {
      showToast('warning', 'Введите номер заказа или телефон');
      return;
    }
    haptic('selection');
    const nextFilters = {
      ...filters,
      order_number: cleanNumber,
      phone: cleanPhone
    };
    setAppliedFilters(nextFilters);
    setQuickView('all');
    setSearchQuery('');
    setDebouncedSearchQuery('');
    setShowFilters(false);
    setIsMobileFiltersOpen(false);
  };

  const clearExactSearch = () => {
    haptic('tap');
    setExactSearchDraft(buildExactSearchDraft());
    const cleanFilters = {
      ...filters,
      order_number: '',
      phone: ''
    };
    setAppliedFilters(cleanFilters);
    setSearchMode('journal');
  };

  const applyFilters = () => {
    if (searchMode === 'exact') {
      applyExactSearch();
      return;
    }

    const nextFilters = {
      ...filters,
      order_number: '',
      phone: ''
    };

    const rangeDays = countDaysInclusive(nextFilters.date_from, nextFilters.date_to);
    if (!rangeDays) {
      showToast('warning', 'Выберите корректный интервал');
      return;
    }
    if (rangeDays > MAX_RANGE_DAYS) {
      showToast('warning', `Журнал можно открыть максимум за ${MAX_RANGE_DAYS} дней`);
      return;
    }

    haptic('selection');
    setAppliedFilters(nextFilters);
    setShowFilters(false);
    setIsMobileFiltersOpen(false);
    setQuickView(nextFilters.status || 'all');
  };

  const isDraftExactSearchMode = searchMode === 'exact' && hasExactSearch(exactSearchDraft);
  const isAppliedExactSearchMode = hasExactSearch(appliedFilters);
  const disableSecondaryControls = isDraftExactSearchMode || isAppliedExactSearchMode;
  const draftFiltersForCompare = useMemo(() => ({
    ...filters,
    order_number: searchMode === 'exact' ? String(exactSearchDraft.order_number || '').trim() : '',
    phone: searchMode === 'exact' ? String(exactSearchDraft.phone || '').trim() : ''
  }), [filters, searchMode, exactSearchDraft.order_number, exactSearchDraft.phone]);
  const hasPendingFilterChanges = !areFiltersEqual(draftFiltersForCompare, appliedFilters);
  const effectiveQuickView = isAppliedExactSearchMode
    ? 'all'
    : (appliedFilters.status && appliedFilters.status !== 'all' ? appliedFilters.status : quickView);
  const effectiveSearchQuery = isAppliedExactSearchMode ? '' : debouncedSearchQuery;

  const ordersMatchingPrimaryFilters = useMemo(() => {
    const normalizedSearch = effectiveSearchQuery.trim().toLowerCase();
    return orders.filter((order) => {
      if (!isAppliedExactSearchMode) {
        if (appliedFilters.service_type !== 'all' && order.service_type !== appliedFilters.service_type) return false;
        if (!hasSelectedStage(order, appliedFilters.stage, appliedFilters.date_target)) return false;
        if (appliedFilters.priority !== 'all' && order.priority !== appliedFilters.priority) return false;
      }
      if (effectiveSearchQuery && !buildSearchHaystack(order).includes(normalizedSearch)) return false;
      return true;
    });
  }, [orders, appliedFilters.service_type, appliedFilters.stage, appliedFilters.priority, appliedFilters.date_target, effectiveSearchQuery, isAppliedExactSearchMode]);

  const assigneeOptions = useMemo(
    () => getAssigneeFilterOptions(
      orders.filter((order) => {
        if (isDraftExactSearchMode) return true;
        if (filters.service_type !== 'all' && order.service_type !== filters.service_type) return false;
        if (!hasSelectedStage(order, filters.stage, filters.date_target)) return false;
        if (filters.priority !== 'all' && order.priority !== filters.priority) return false;
        return true;
      }),
      filters.stage
    ),
    [orders, filters.service_type, filters.stage, filters.priority, filters.date_target, isDraftExactSearchMode]
  );

  useEffect(() => {
    if (isDraftExactSearchMode || appliedFilters.assignee === 'all') return;
    if (!assigneeOptions.some((option) => option.value === filters.assignee)) {
      setFilters((prev) => ({ ...prev, assignee: 'all' }));
    }
  }, [assigneeOptions, filters.assignee, appliedFilters.assignee, isDraftExactSearchMode]);

  const ordersMatchingFacets = useMemo(() => {
    if (isAppliedExactSearchMode || appliedFilters.assignee === 'all' || appliedFilters.date_target === 'stage_completed') {
      return ordersMatchingPrimaryFilters;
    }
    return ordersMatchingPrimaryFilters.filter((order) => {
      const entries = getRelevantAssigneeEntries(order, appliedFilters.stage);
      if (appliedFilters.assignee === 'unassigned') return entries.length === 0;
      return entries.some((entry) => String(entry.id) === String(appliedFilters.assignee));
    });
  }, [ordersMatchingPrimaryFilters, appliedFilters.assignee, appliedFilters.stage, appliedFilters.date_target, isAppliedExactSearchMode]);

  const visibleOrders = useMemo(() => {
    const scoped = ordersMatchingFacets.filter((order) => {
      if (effectiveQuickView === 'all') return true;
      if (effectiveQuickView === 'deferred') return getLifecycleKey(order) === 'deferred';
      if (effectiveQuickView === 'unassigned') return getLifecycleKey(order) === 'unassigned';
      if (effectiveQuickView === 'repeat') return getLifecycleKey(order) === 'repeat';
      if (effectiveQuickView === 'completed') return order.status === 'completed';
      if (effectiveQuickView === 'cancelled') return order.status === 'cancelled';
      return getLifecycleKey(order) === 'active';
    });

    const sorted = [...scoped];
    sorted.sort((left, right) => {
      switch (appliedFilters.sort) {
        case 'created_asc':
          return getSortTimestamp(left.created_date) - getSortTimestamp(right.created_date);
        case 'pickup_asc':
          return getSortTimestamp(getEffectivePickupDate(left)) - getSortTimestamp(getEffectivePickupDate(right));
        case 'return_asc':
          return getSortTimestamp(getEffectiveDeliveryDate(left)) - getSortTimestamp(getEffectiveDeliveryDate(right));
        case 'created_desc':
        default:
          return getSortTimestamp(right.created_date) - getSortTimestamp(left.created_date);
      }
    });
    return sorted;
  }, [ordersMatchingFacets, effectiveQuickView, appliedFilters.sort]);

  const selectedTotalCount = orderCounts[effectiveQuickView];

  useEffect(() => {
    if (loading || selectedTotalCount === null || selectedTotalCount === undefined) return;
    setHasMore(orders.length < selectedTotalCount);
  }, [loading, orders.length, selectedTotalCount]);

  const activeFilterCount = useMemo(() => {
    let count = 0;
    if (appliedFilters.service_type !== 'all') count += 1;
    if (appliedFilters.client_type !== 'all') count += 1;
    if (appliedFilters.department !== 'all') count += 1;
    if (appliedFilters.stage !== 'all') count += 1;
    if (appliedFilters.status && appliedFilters.status !== 'all') count += 1;
    if (appliedFilters.priority !== 'all') count += 1;
    if (appliedFilters.assignee !== 'all') count += 1;
    if (appliedFilters.date_target === 'stage_completed') count += 1;
    return count;
  }, [appliedFilters]);

  const draftFilterCount = useMemo(() => {
    let count = 0;
    if (filters.service_type !== 'all') count += 1;
    if (filters.client_type !== 'all') count += 1;
    if (filters.department !== 'all') count += 1;
    if (filters.stage !== 'all') count += 1;
    if (filters.status && filters.status !== 'all') count += 1;
    if (filters.priority !== 'all') count += 1;
    if (filters.assignee !== 'all') count += 1;
    if (filters.date_target === 'stage_completed') count += 1;
    return count;
  }, [filters]);

  const selectedStageOption = visibleStageFilterOptions.find((option) => option.value === filters.stage) || visibleStageFilterOptions[0];
  const appliedStageOption = visibleStageFilterOptions.find((option) => option.value === appliedFilters.stage) || visibleStageFilterOptions[0];
  const selectedSortOption = SORT_OPTIONS.find((option) => option.value === filters.sort) || SORT_OPTIONS[0];
  const activeSortOption = SORT_OPTIONS.find((option) => option.value === appliedFilters.sort) || SORT_OPTIONS[0];
  const ActiveSortIcon = activeSortOption.icon || Calendar;
  const currentIntervalLabel = isAppliedExactSearchMode
    ? (
      String(appliedFilters.order_number || '').trim()
        ? `Поиск: №${String(appliedFilters.order_number || '').trim()}`
        : `Поиск: ${formatPhone(appliedFilters.phone)}`
    )
    : formatIntervalLabel(appliedFilters.date_from, appliedFilters.date_to);
  const currentRangeDays = countDaysInclusive(filters.date_from, filters.date_to);
  const departmentOptions = useMemo(() => ([
    { value: 'all', label: 'Все подразделения', icon: Building2 },
    ...departments.map((department) => ({
      value: String(department.id),
      label: department.name || `Подразделение #${department.id}`,
      icon: Building2
    }))
  ]), [departments]);

  const closeAssignModal = useCallback(() => {
    setAssignModalOrder(null);
    setAvailableAssignees([]);
    setAvailableAssigneeFlows([]);
    setAssigneeStageDraft('');
    setAssigneeDraft(UNASSIGN_ASSIGNEE_VALUE);
  }, []);

  const openBulkModal = useCallback(async () => {
    haptic('open');
    setBulkModalOpen(true);
    setBulkOptionsLoading(true);
    setBulkStages([]);
    setBulkStageDraft('');
    setBulkAssigneeDraft('');

    try {
      const data = await requestJson(`${API_URL}/api/orders/bulk-unassign/options`);
      const stages = Array.isArray(data?.stages) ? data.stages : [];
      const preferredStage = stages.find((stage) => stage.stage_name === appliedFilters.stage)
        || stages.find((stage) => stage.stage_name === 'all')
        || null;
      const preferredAssignee = preferredStage?.assignees?.find(
        (assignee) => String(assignee.id) === String(appliedFilters.assignee)
      ) || null;
      setBulkStages(stages);
      setBulkStageDraft(preferredStage?.stage_name || '');
      setBulkAssigneeDraft(preferredAssignee ? String(preferredAssignee.id) : '');
    } catch (error) {
      closeBulkModal();
      showToast('error', error.message);
    } finally {
      setBulkOptionsLoading(false);
    }
  }, [appliedFilters.assignee, appliedFilters.stage, closeBulkModal, showToast]);

  const openPrintModal = useCallback(() => {
    haptic('open');
    resetPreparedPrint();
    const filterStageIsPrintable = visiblePrintStageOptions.some((option) => option.value === appliedFilters.stage);
    if (filterStageIsPrintable) {
      setPrintStageDraft(appliedFilters.stage);
    } else if (!isDryCleaningEnabled && printStageDraft === 'cleaning') {
      setPrintStageDraft('washing');
    }
    setPrintOrderStatusesDraft([PRINT_STATUS_BY_QUICK_VIEW[quickView] || 'all']);
    setPrintModalOpen(true);
  }, [appliedFilters.stage, isDryCleaningEnabled, printStageDraft, quickView, resetPreparedPrint, visiblePrintStageOptions]);

  const prepareOrderWorkSheet = useCallback(async () => {
    const resolvedDateTo = printRangeMode === 'date' ? printDateFrom : printDateTo;
    const rangeDays = countDaysInclusive(printDateFrom, resolvedDateTo);
    const isAssemblySheet = printDocumentTypeDraft === 'assembly';
    if ((!isAssemblySheet && !printStageDraft) || !printDateFrom || !resolvedDateTo) {
      showToast('error', isAssemblySheet ? 'Выберите дату доставки' : 'Выберите этап и дату печати');
      return;
    }
    if (rangeDays < 1) {
      showToast('error', 'Дата окончания периода не может быть раньше даты начала');
      return;
    }
    if (rangeDays > MAX_RANGE_DAYS) {
      showToast('error', `Для одного PDF можно выбрать не более ${MAX_RANGE_DAYS} дней`);
      return;
    }

    haptic('selection');
    resetPreparedPrint();
    setPrintLoading(true);
    try {
      const params = isAssemblySheet
        ? new URLSearchParams({
          date_from: printDateFrom,
          date_to: resolvedDateTo,
          sort_mode: printAssemblySortDraft
        })
        : new URLSearchParams({
          stage_name: printStageDraft,
          lifecycle_status: printOrderStatusesDraft.join(','),
          compact: printCompactDraft ? 'true' : 'false',
          date_from: printDateFrom,
          date_to: resolvedDateTo
        });
      const endpoint = isAssemblySheet ? 'assembly-sheet-pdf' : 'work-sheet-pdf';
      const { blob, response } = await fetchProtectedBlobWithResponse(`${API_URL}/api/orders/${endpoint}?${params.toString()}`);
      const countHeader = isAssemblySheet ? 'X-Assembly-Sheet-Items-Count' : 'X-Work-Sheet-Orders-Count';
      const taskCount = Number.parseInt(response.headers.get(countHeader) || '', 10);
      setPrintObjectUrl(URL.createObjectURL(blob));
      setPrintFileName(
        isAssemblySheet
          ? buildAssemblySheetFileName(printDateFrom, resolvedDateTo)
          : buildOrderWorkSheetFileName(printStageDraft, printDateFrom, resolvedDateTo, printCompactDraft)
      );
      setPrintTaskCount(Number.isFinite(taskCount) ? taskCount : null);
      showToast('success', isAssemblySheet ? 'Сборочный PDF готов' : 'Рабочий PDF готов');
    } catch (error) {
      showToast('error', error.message || 'Не удалось сформировать PDF');
    } finally {
      setPrintLoading(false);
    }
  }, [printAssemblySortDraft, printCompactDraft, printDateFrom, printDateTo, printDocumentTypeDraft, printOrderStatusesDraft, printRangeMode, printStageDraft, resetPreparedPrint, showToast]);

  const openPreparedWorkSheet = useCallback(() => {
    if (!printObjectUrl) return;
    haptic('selection');
    const openedWindow = window.open(printObjectUrl, '_blank');
    if (!openedWindow) {
      showToast('error', 'Браузер заблокировал открытие PDF. Разрешите всплывающие окна для этого сайта.');
      return;
    }
    openedWindow.opener = null;
  }, [printObjectUrl, showToast]);

  const savePreparedWorkSheet = useCallback(() => {
    if (!printObjectUrl) return;
    haptic('selection');
    const link = document.createElement('a');
    link.href = printObjectUrl;
    link.download = printFileName || 'Рабочий_лист.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    showToast('success', 'PDF сохранён');
  }, [printFileName, printObjectUrl, showToast]);

  const printPreparedWorkSheet = useCallback(() => {
    if (!printObjectUrl) return;
    haptic('selection');
    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      showToast('error', 'Браузер заблокировал окно печати. Разрешите всплывающие окна для этого сайта.');
      return;
    }
    printWindow.opener = null;
    printWindow.document.write(`
      <html lang="ru">
        <head><title>Печать рабочего листа</title></head>
        <body style="margin:0;padding:24px;font-family:system-ui,-apple-system,sans-serif;color:#0f172a;">
          <div style="font-size:14px;">Открываем PDF для печати...</div>
        </body>
      </html>
    `);
    printWindow.document.close();
    const triggerPrint = () => {
      try {
        printWindow.focus();
        printWindow.print();
      } catch (printError) {
        console.warn('Не удалось автоматически открыть печать рабочего листа', printError);
      }
    };
    printWindow.onload = () => window.setTimeout(triggerPrint, 350);
    printWindow.location.href = printObjectUrl;
    window.setTimeout(triggerPrint, 1800);
  }, [printObjectUrl, showToast]);

  const openAssignModal = useCallback(async (order) => {
    haptic('open');
    setAssignModalOrder(order);
    setAssigneesLoading(true);
    setAvailableAssignees([]);
    setAvailableAssigneeFlows([]);
    setAssigneeStageDraft('');
    setAssigneeDraft(UNASSIGN_ASSIGNEE_VALUE);

    try {
      const data = await requestJson(`${API_URL}/api/orders/${order.id}/available-assignees`);
      if (data?.is_flow_assignment && Array.isArray(data.flows) && data.flows.length > 0) {
        const flows = data.flows;
        const preferredFlow = !isAppliedExactSearchMode && FLOW_STAGE_NAMES.includes(appliedFilters.stage)
          ? flows.find((flow) => flow.stage_name === appliedFilters.stage)
          : null;
        const initialFlow = preferredFlow || flows.find((flow) => flow.can_assign !== false) || flows[0] || null;
        setAvailableAssigneeFlows(flows);
        setAvailableAssignees([]);
        setAssigneeStageDraft(initialFlow?.stage_name || '');
        setAssigneeDraft(initialFlow?.assigned_to ? String(initialFlow.assigned_to) : UNASSIGN_ASSIGNEE_VALUE);
      } else {
        setAvailableAssigneeFlows([]);
        setAvailableAssignees(Array.isArray(data?.users) ? data.users : []);
        setAssigneeDraft(order?.assigned_to ? String(order.assigned_to) : UNASSIGN_ASSIGNEE_VALUE);
      }
    } catch (error) {
      closeAssignModal();
      showToast('error', error.message);
    } finally {
      setAssigneesLoading(false);
    }
  }, [appliedFilters.stage, closeAssignModal, isAppliedExactSearchMode, showToast]);

  const openOrder = useCallback((order) => {
    haptic('tap');
    navigate(`/order/${order.id}?from=orders-log`);
  }, [navigate]);

  const cycleLocalSort = useCallback(() => {
    const currentIndex = SORT_OPTIONS.findIndex((option) => option.value === appliedFilters.sort);
    const nextIndex = currentIndex >= 0 ? (currentIndex + 1) % SORT_OPTIONS.length : 0;
    const nextSort = SORT_OPTIONS[nextIndex]?.value || SORT_OPTIONS[0].value;
    setAppliedFilters((prev) => ({ ...prev, sort: nextSort }));
    setFilters((prev) => ({ ...prev, sort: nextSort }));
  }, [appliedFilters.sort]);

  const handleLoadMore = useCallback(async () => {
    if (loading || loadingMore || loadMoreInFlightRef.current || !hasMore) return;

    const requestKey = buildOrderLogListKey(appliedFilters, effectiveQuickView, effectiveSearchQuery);
    if (activeListKeyRef.current !== requestKey) return;

    loadMoreInFlightRef.current = true;
    setLoadingMore(true);
    try {
      const data = await requestOrdersList(appliedFilters, {
        offset: orders.length,
        quickView: effectiveQuickView,
        searchQuery: effectiveSearchQuery
      });
      if (activeListKeyRef.current !== requestKey) return;

      const page = Array.isArray(data) ? data : [];
      const nextOrders = mergeOrdersById(orders, page);
      setOrders(nextOrders);
      setHasMore(selectedTotalCount === null || selectedTotalCount === undefined
        ? page.length === ORDER_LOG_PAGE_SIZE
        : nextOrders.length < selectedTotalCount);
    } catch (error) {
      if (activeListKeyRef.current === requestKey) {
        showToast('error', error.message || 'Не удалось загрузить следующие заказы');
      }
    } finally {
      if (activeListKeyRef.current === requestKey) {
        setLoadingMore(false);
      }
      loadMoreInFlightRef.current = false;
    }
  }, [appliedFilters, effectiveQuickView, effectiveSearchQuery, hasMore, loading, loadingMore, orders, selectedTotalCount, showToast]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel || !hasMore || loading || loadingMore || typeof IntersectionObserver === 'undefined') {
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) handleLoadMore();
      },
      { rootMargin: '360px 0px' }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [handleLoadMore, hasMore, loading, loadingMore]);

  const isFlowAssigneeSelection = availableAssigneeFlows.length > 0;
  const selectedAssigneeFlow = useMemo(() => (
    isFlowAssigneeSelection
      ? (availableAssigneeFlows.find((flow) => flow.stage_name === assigneeStageDraft) || availableAssigneeFlows[0] || null)
      : null
  ), [isFlowAssigneeSelection, availableAssigneeFlows, assigneeStageDraft]);

  const selectedAssigneeUsers = useMemo(() => (
    isFlowAssigneeSelection
      ? (Array.isArray(selectedAssigneeFlow?.users) ? selectedAssigneeFlow.users : [])
      : availableAssignees
  ), [isFlowAssigneeSelection, selectedAssigneeFlow, availableAssignees]);

  const selectedAssigneeCurrentValue = isFlowAssigneeSelection
    ? String(selectedAssigneeFlow?.assigned_to || UNASSIGN_ASSIGNEE_VALUE)
    : String(assignModalOrder?.assigned_to || UNASSIGN_ASSIGNEE_VALUE);

  useEffect(() => {
    if (!assignModalOrder || !isFlowAssigneeSelection) return;
    const nextFlow = availableAssigneeFlows.find((flow) => flow.stage_name === assigneeStageDraft)
      || availableAssigneeFlows[0]
      || null;
    if (!nextFlow) return;
    if (nextFlow.stage_name !== assigneeStageDraft) {
      setAssigneeStageDraft(nextFlow.stage_name);
      return;
    }
    setAssigneeDraft(nextFlow.assigned_to ? String(nextFlow.assigned_to) : UNASSIGN_ASSIGNEE_VALUE);
  }, [assignModalOrder, isFlowAssigneeSelection, availableAssigneeFlows, assigneeStageDraft]);

  const saveAssignee = async () => {
    if (!assignModalOrder) return;
    setAssignmentSaving(true);
    try {
      const assigneeId = assigneeDraft && assigneeDraft !== UNASSIGN_ASSIGNEE_VALUE ? Number(assigneeDraft) : null;
      if (isFlowAssigneeSelection && selectedAssigneeFlow && ['washing_laundry', 'washing_cleaning'].includes(selectedAssigneeFlow.stage_name)) {
        await requestJson(`${API_URL}/api/orders/${assignModalOrder.id}/washing-flows/${selectedAssigneeFlow.stage_name}/assignee`, {
          method: 'PUT',
          body: JSON.stringify({ assigned_to: assigneeId })
        });
        showToast('success', assigneeId ? 'Исполнитель потока назначен' : 'Исполнитель потока снят');
      } else {
        await requestJson(`${API_URL}/api/orders/${assignModalOrder.id}`, {
          method: 'PUT',
          body: JSON.stringify({ assigned_to: assigneeId })
        });
        showToast('success', assigneeId ? 'Исполнитель назначен' : 'Исполнитель снят');
      }

      closeAssignModal();
      await reloadOrders({ silent: true });
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setAssignmentSaving(false);
    }
  };

  const assigneeSaveDisabled = assignmentSaving
    || assigneesLoading
    || (isFlowAssigneeSelection && !selectedAssigneeFlow)
    || String(assigneeDraft || UNASSIGN_ASSIGNEE_VALUE) === String(selectedAssigneeCurrentValue);

  const selectedBulkStage = bulkStages.find((stage) => stage.stage_name === bulkStageDraft) || null;
  const allBulkAssigneesTarget = buildAllBulkAssigneesTarget(selectedBulkStage);
  const selectedBulkAssignee = bulkAssigneeDraft === ALL_BULK_ASSIGNEES_VALUE
    ? allBulkAssigneesTarget
    : selectedBulkStage?.assignees?.find(
      (assignee) => String(assignee.id) === String(bulkAssigneeDraft)
    ) || null;
  const bulkStageOptions = bulkStages.map((stage) => ({
    value: stage.stage_name,
    label: `${stage.label} · ${stage.assignments_count}`,
    icon: STAGES_CONFIG[stage.stage_name]?.icon || Package
  }));
  const bulkAssigneeOptions = [
    ...(allBulkAssigneesTarget ? [{
      value: ALL_BULK_ASSIGNEES_VALUE,
      label: `Все исполнители · ${allBulkAssigneesTarget.assignments_count}`,
      icon: UserMinus
    }] : []),
    ...(selectedBulkStage?.assignees || []).map((assignee) => ({
      value: String(assignee.id),
      label: `${assignee.name} · ${assignee.assignments_count}`,
      icon: UserCheck
    }))
  ];
  const resolvedPrintDateTo = printRangeMode === 'date' ? printDateFrom : printDateTo;
  const printRangeDays = countDaysInclusive(printDateFrom, resolvedPrintDateTo);
  const printSubmitDisabled = printLoading
    || (printDocumentTypeDraft === 'work' && !printStageDraft)
    || !printDateFrom
    || !resolvedPrintDateTo
    || printRangeDays < 1
    || printRangeDays > MAX_RANGE_DAYS;

  const saveBulkUnassign = async () => {
    if (!selectedBulkStage || !selectedBulkAssignee) return;
    setBulkSaving(true);
    try {
      const response = await requestJson(`${API_URL}/api/orders/bulk-unassign`, {
        method: 'POST',
        body: JSON.stringify({
          stage_name: selectedBulkStage.stage_name,
          assignee_id: selectedBulkAssignee.all_assignees ? null : Number(selectedBulkAssignee.id)
        })
      });
      showToast('success', response?.message || 'Назначения сняты');
      closeBulkModal();
      await reloadOrders({ silent: true });
    } catch (error) {
      showToast('error', error.message);
    } finally {
      setBulkSaving(false);
    }
  };

  const requestBulkUnassignConfirmation = () => {
    if (!selectedBulkStage || !selectedBulkAssignee || bulkSaving) return;
    setBulkConfirmOpen(true);
  };

  return (
    <div className="space-y-5 desktop-content-container-wide">
      {/* Search & Filters Section */}
      <section className="rounded-3xl bg-white p-4 shadow-sm dark:bg-gray-900 sm:p-6">
        <div className="flex flex-col gap-4">
          {/* Top Mode Switcher & Tools */}
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between border-b border-gray-100 pb-3.5 dark:border-gray-800">
            <div className="flex items-center justify-between sm:justify-start gap-2">
              <div className="w-full grid grid-cols-2 rounded-2xl bg-gray-100/80 p-1 dark:bg-gray-800/80 sm:w-auto sm:inline-flex sm:flex-row">
                <button
                  type="button"
                  onClick={() => handleSwitchMode('journal')}
                  className={`inline-flex items-center justify-center gap-1.5 sm:gap-2 rounded-xl px-2.5 sm:px-3.5 py-2 text-xs sm:text-sm font-semibold transition-all ${
                    searchMode === 'journal'
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  <Calendar className="h-4 w-4 shrink-0" />
                  <span>Журнал по периоду</span>
                </button>
                <button
                  type="button"
                  onClick={() => handleSwitchMode('exact')}
                  className={`inline-flex items-center justify-center gap-1.5 sm:gap-2 rounded-xl px-2.5 sm:px-3.5 py-2 text-xs sm:text-sm font-semibold transition-all ${
                    searchMode === 'exact'
                      ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                      : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  <Search className="h-4 w-4 shrink-0" />
                  <span>По номеру / телефону</span>
                </button>
              </div>

              {/* Mobile Tools Popout Trigger */}
              <div className="relative sm:hidden" ref={toolsRef}>
                <button
                  type="button"
                  onClick={() => {
                    haptic('tap');
                    setShowTools((prev) => !prev);
                  }}
                  className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors ${
                    showTools
                      ? 'border-slate-800 bg-slate-900 text-white shadow-sm dark:border-slate-200 dark:bg-slate-100 dark:text-slate-900'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                  }`}
                  title="Инструменты журнала"
                  aria-label="Инструменты журнала"
                >
                  <Wrench className="h-4 w-4" />
                </button>

                <AnimatePresence>
                  {showTools && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.9, y: -4 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.9, y: -4 }}
                      transition={{ duration: 0.15 }}
                      className="absolute right-0 top-full mt-2 z-40 flex flex-col gap-1.5 rounded-2xl border border-gray-200 bg-white p-2 shadow-xl dark:border-gray-700 dark:bg-gray-900 min-w-[190px]"
                    >
                      <button
                        type="button"
                        onClick={() => {
                          setShowTools(false);
                          openBulkModal();
                        }}
                        disabled={loading || bulkOptionsLoading}
                        className="inline-flex h-9 items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 text-xs font-semibold text-amber-700 transition hover:bg-amber-100 disabled:opacity-50 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-300"
                      >
                        <UserMinus className="h-4 w-4 shrink-0" />
                        <span className="whitespace-nowrap">Массовое снятие</span>
                      </button>

                      <button
                        type="button"
                        onClick={() => {
                          setShowTools(false);
                          openPrintModal();
                        }}
                        disabled={printLoading}
                        className="inline-flex h-9 items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 text-xs font-semibold text-blue-700 transition hover:bg-blue-100 disabled:opacity-50 dark:border-blue-900/70 dark:bg-blue-950/30 dark:text-blue-300"
                        title="Печать"
                      >
                        <Printer className="h-4 w-4 shrink-0" />
                        <span className="whitespace-nowrap">Печать</span>
                      </button>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>

            {/* Desktop Actions in Journal Mode */}
            {searchMode === 'journal' && (
              <div className="hidden lg:flex items-center gap-2">
                {/* Desktop Sliding Tools Cluster */}
                <div className="relative flex items-center gap-1.5" ref={desktopToolsRef}>
                  <AnimatePresence>
                    {showTools && (
                      <motion.div
                        initial={{ opacity: 0, width: 0, x: 10 }}
                        animate={{ opacity: 1, width: 'auto', x: 0 }}
                        exit={{ opacity: 0, width: 0, x: 10 }}
                        transition={{ duration: 0.2 }}
                        className="flex items-center gap-1.5 overflow-hidden"
                      >
                        <button
                          type="button"
                          onClick={openBulkModal}
                          disabled={loading || bulkOptionsLoading}
                          className="inline-flex h-10 items-center gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 text-xs font-semibold text-amber-700 transition hover:bg-amber-100 disabled:opacity-50 dark:border-amber-900/70 dark:bg-amber-950/30 dark:text-amber-300"
                          title="Массово снять назначения"
                        >
                          <UserMinus className="h-4 w-4 shrink-0" />
                          <span className="whitespace-nowrap">Массовое снятие</span>
                        </button>

                        <button
                          type="button"
                          onClick={openPrintModal}
                          disabled={printLoading}
                          className="inline-flex h-10 items-center gap-2 rounded-xl border border-blue-200 bg-blue-50 px-3 text-xs font-semibold text-blue-700 transition hover:bg-blue-100 disabled:opacity-50 dark:border-blue-900/70 dark:bg-blue-950/30 dark:text-blue-300"
                          title="Сформировать рабочий PDF"
                        >
                          <Printer className="h-4 w-4 shrink-0" />
                          <span className="whitespace-nowrap">Печать</span>
                        </button>
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <button
                    type="button"
                    onClick={() => {
                      haptic('tap');
                      setShowTools((prev) => !prev);
                    }}
                    className={`inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border transition-colors ${
                      showTools
                        ? 'border-slate-800 bg-slate-900 text-white shadow-sm dark:border-slate-200 dark:bg-slate-100 dark:text-slate-900'
                        : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                    }`}
                    title="Инструменты журнала"
                    aria-label="Инструменты журнала"
                  >
                    <Wrench className="h-4 w-4" />
                  </button>
                </div>

                <div className="h-6 w-px bg-gray-200 dark:bg-gray-700 mx-1" />

                <button
                  type="button"
                  onClick={resetAndApplyAllFilters}
                  disabled={loading}
                  className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800"
                  title="Сбросить все фильтры к значениям по умолчанию"
                >
                  <RotateCcw className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  onClick={() => setShowFilters((prev) => !prev)}
                  className={`inline-flex h-10 items-center gap-2 rounded-xl border px-3.5 text-xs font-semibold transition-colors ${
                    showFilters || activeFilterCount > 0
                      ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300'
                      : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'
                  }`}
                >
                  <Filter className="h-4 w-4" />
                  <span>Фильтры</span>
                  {activeFilterCount > 0 && (
                    <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-blue-600 px-1.5 text-[11px] font-semibold text-white dark:bg-blue-500">
                      {activeFilterCount}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  onClick={applyFilters}
                  disabled={loading}
                  className={`inline-flex h-10 items-center gap-2 rounded-xl bg-blue-600 px-5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-60 dark:bg-blue-600 dark:hover:bg-blue-500 ${
                    hasPendingFilterChanges ? 'ring-2 ring-blue-400/40' : ''
                  }`}
                >
                  <Search className="h-4 w-4" />
                  <span>Применить</span>
                </button>
              </div>
            )}
          </div>

          {/* EXACT SEARCH VIEW */}
          {searchMode === 'exact' ? (
            <div className="space-y-4">
              <div className="text-sm sm:text-base font-bold text-gray-900 dark:text-gray-100">
                Точный поиск по номеру или телефону
              </div>

              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <FloatingField label="Номер заказа">
                  <div className="relative">
                    <input
                      type="text"
                      inputMode="numeric"
                      value={exactSearchDraft.order_number}
                      onChange={(event) => {
                        const value = event.target.value;
                        setExactSearchDraft({ order_number: value, phone: value ? '' : exactSearchDraft.phone });
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') applyExactSearch();
                      }}
                      placeholder="Номер заказа"
                      className="h-11 w-full bg-transparent px-3 pr-9 text-sm text-gray-900 outline-none transition dark:text-gray-100"
                    />
                    {!!String(exactSearchDraft.order_number || '').trim() && (
                      <button
                        type="button"
                        onClick={() => setExactSearchDraft((prev) => ({ ...prev, order_number: '' }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        title="Очистить"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </FloatingField>

                <FloatingField label="Номер телефона">
                  <div className="relative">
                    <input
                      type="tel"
                      value={exactSearchDraft.phone}
                      onChange={(event) => {
                        const value = formatPhoneInput(event.target.value);
                        setExactSearchDraft({ phone: value, order_number: value ? '' : exactSearchDraft.order_number });
                      }}
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') applyExactSearch();
                      }}
                      placeholder="Номер телефона"
                      className="h-11 w-full bg-transparent px-3 pr-9 text-sm text-gray-900 outline-none transition dark:text-gray-100"
                    />
                    {!!String(exactSearchDraft.phone || '').trim() && (
                      <button
                        type="button"
                        onClick={() => setExactSearchDraft((prev) => ({ ...prev, phone: '' }))}
                        className="absolute right-2 top-1/2 -translate-y-1/2 rounded-lg p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                        title="Очистить"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </FloatingField>
              </div>

              <div className="flex items-center justify-end gap-2 pt-1">
                <button
                  type="button"
                  onClick={() => setExactSearchDraft({ order_number: '', phone: '' })}
                  disabled={!exactSearchDraft.order_number && !exactSearchDraft.phone}
                  className="rounded-xl border border-gray-200 bg-white px-4 py-2.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 disabled:opacity-40 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300 dark:hover:bg-gray-700"
                >
                  Очистить
                </button>
                <button
                  type="button"
                  onClick={applyExactSearch}
                  disabled={!exactSearchDraft.order_number && !exactSearchDraft.phone}
                  className="inline-flex items-center justify-center gap-2 rounded-xl bg-blue-600 px-5 py-2.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:opacity-40"
                >
                  <Search className="h-4 w-4" />
                  <span>Найти</span>
                </button>
              </div>
            </div>
          ) : (
            /* JOURNAL VIEW */
            <>
              {/* Desktop Presets & Date Target Row */}
              <div className="hidden lg:flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1.5">
                    {[
                      { key: 'today', label: 'Сегодня' },
                      { key: 'week', label: '7 дней' },
                      { key: '30d', label: '30 дней' },
                      { key: 'month', label: 'Текущий месяц' }
                    ].map((preset) => {
                      const active = datePreset === preset.key;
                      return (
                        <button
                          key={preset.key}
                          type="button"
                          onClick={() => applyDatePreset(preset.key)}
                          className={`h-9 rounded-xl px-3 text-xs font-medium transition-colors ${
                            active
                              ? 'border border-blue-200 bg-blue-50 text-blue-700 shadow-sm dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
                              : 'border border-gray-200 bg-gray-50/80 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-300'
                          }`}
                        >
                          {preset.label}
                        </button>
                      );
                    })}
                  </div>

                  <div className="flex items-center gap-1.5 pl-2 border-l border-gray-200 dark:border-gray-700">
                    <div
                      className="inline-flex h-9 items-center rounded-xl border border-gray-200 bg-gray-50/80 p-0.5 dark:border-gray-700 dark:bg-gray-800/70"
                      title="Режим дат"
                    >
                      <span className="sr-only">Режим дат</span>
                      <button
                        type="button"
                        onClick={() => {
                          setFilters((prev) => ({ ...prev, date_mode: 'period' }));
                        }}
                        className={`h-full rounded-lg px-2.5 text-xs font-semibold transition-colors ${
                          filters.date_mode !== 'date'
                            ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                            : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                      >
                        Период
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          setFilters((prev) => ({ ...prev, date_mode: 'date', date_to: prev.date_from }));
                        }}
                        className={`h-full rounded-lg px-2.5 text-xs font-semibold transition-colors ${
                          filters.date_mode === 'date'
                            ? 'bg-white text-blue-600 shadow-sm dark:bg-gray-900 dark:text-blue-400'
                            : 'text-gray-500 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'
                        }`}
                      >
                        Одна дата
                      </button>
                    </div>

                    {filters.date_mode === 'date' ? (
                      <div className="w-40 xl:w-44">
                        <DatePicker
                          value={filters.date_from}
                          onChange={(val) => {
                            updateDateFilter('date_from', val);
                            updateDateFilter('date_to', val);
                          }}
                          placeholder="Дата"
                          allowPastDates
                          showTodayButton={false}
                          inputClassName="h-9 rounded-xl border-gray-200 bg-white text-xs dark:border-gray-700 dark:bg-gray-900"
                        />
                      </div>
                    ) : (
                      <>
                        <div className="w-40 xl:w-44">
                          <DatePicker
                            value={filters.date_from}
                            onChange={(val) => updateDateFilter('date_from', val)}
                            placeholder="От"
                            allowPastDates
                            showTodayButton={false}
                            inputClassName="h-9 rounded-xl border-gray-200 bg-white text-xs dark:border-gray-700 dark:bg-gray-900"
                          />
                        </div>
                        <span className="text-xs text-gray-400">—</span>
                        <div className="w-40 xl:w-44">
                          <DatePicker
                            value={filters.date_to}
                            onChange={(val) => updateDateFilter('date_to', val)}
                            placeholder="До"
                            allowPastDates
                            showTodayButton={false}
                            inputClassName="h-9 rounded-xl border-gray-200 bg-white text-xs dark:border-gray-700 dark:bg-gray-900"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-2.5">
                  <div className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-gray-50/80 px-3 py-1.5 text-xs text-gray-700 dark:border-gray-700 dark:bg-gray-800/60 dark:text-gray-300">
                    <Calendar className="h-3.5 w-3.5 text-gray-400" />
                    <span className="font-semibold">{currentIntervalLabel}</span>
                  </div>
                  {appliedFilters.date_target === 'stage_completed' && (
                    <span className="shrink-0 rounded-lg bg-blue-100 px-2.5 py-1 text-xs font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                      По этапу
                    </span>
                  )}
                </div>
              </div>

              {/* Mobile Presets & Summary Row */}
              <div className="flex flex-col gap-2.5 lg:hidden">
                {/* 4 Presets in a clean grid */}
                <div className="grid grid-cols-4 gap-1.5">
                  {[
                    { key: 'today', label: 'Сегодня' },
                    { key: 'week', label: '7 дней' },
                    { key: '30d', label: '30 дней' },
                    { key: 'month', label: 'Месяц' }
                  ].map((preset) => {
                    const active = datePreset === preset.key;
                    return (
                      <button
                        key={preset.key}
                        type="button"
                        onClick={() => applyDatePreset(preset.key)}
                        className={`h-8 rounded-xl px-1 text-[11px] font-medium transition-colors truncate ${
                          active
                            ? 'border border-blue-200 bg-blue-50 text-blue-700 shadow-sm dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
                            : 'border border-gray-200 bg-gray-50/90 text-gray-600 hover:bg-gray-100 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-300'
                        }`}
                      >
                        {preset.label}
                      </button>
                    );
                  })}
                </div>

                {/* Date interval badge */}
                <div className="flex items-center justify-between gap-2 px-1 text-xs text-gray-600 dark:text-gray-300">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <Calendar className="h-3.5 w-3.5 shrink-0 text-blue-500" />
                    <span className="truncate font-semibold text-gray-800 dark:text-gray-200">{currentIntervalLabel}</span>
                  </div>
                  {appliedFilters.date_target === 'stage_completed' && (
                    <span className="shrink-0 rounded-md bg-blue-100 px-2 py-0.5 text-[10px] font-semibold text-blue-700 dark:bg-blue-900/40 dark:text-blue-300">
                      По этапу
                    </span>
                  )}
                </div>

                {/* Mobile Action Buttons: Reset + Filters Drawer Button + Find Button */}
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={resetAndApplyAllFilters}
                    disabled={loading}
                    className="inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-gray-200 bg-white text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200"
                    title="Сбросить все фильтры"
                  >
                    <RotateCcw className="h-4 w-4" />
                  </button>

                  <button
                    type="button"
                    onClick={() => setIsMobileFiltersOpen(true)}
                    className={`relative inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl border px-3 text-xs font-semibold transition-colors ${
                      activeFilterCount > 0
                        ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/40 dark:text-blue-300'
                        : 'border-gray-200 bg-white text-gray-700 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200'
                    }`}
                  >
                    <Filter className="h-3.5 w-3.5" />
                    <span>Фильтры</span>
                    {activeFilterCount > 0 && (
                      <span className="inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-blue-600 px-1 text-[10px] font-bold text-white">
                        {activeFilterCount}
                      </span>
                    )}
                  </button>

                  <button
                    type="button"
                    onClick={applyFilters}
                    disabled={loading}
                    className="inline-flex h-10 flex-1 items-center justify-center gap-2 rounded-xl bg-blue-600 px-3.5 text-xs font-semibold text-white shadow-sm transition hover:bg-blue-700"
                  >
                    <Search className="h-3.5 w-3.5" />
                    <span>Найти</span>
                  </button>
                </div>
              </div>

              {/* Desktop Collapsible Filters Accordion */}
              {showFilters && (
                <div className="hidden lg:block rounded-2xl border border-gray-200 bg-gray-50/70 p-4 dark:border-gray-800 dark:bg-gray-950/40">
                  <div className="grid grid-cols-4 gap-3">
                    <div>
                      <Dropdown
                        label="Этап / поток"
                        buttonClassName="h-11 min-h-[44px] !py-0"
                        value={filters.stage}
                        onChange={(val) => setFilters((prev) => ({ ...prev, stage: String(val), assignee: 'all' }))}
                        options={visibleStageFilterOptions}
                        placeholder="Этап или поток"
                      />
                    </div>

                    <div>
                      <Dropdown
                        label="Статус заказа"
                        buttonClassName="h-11 min-h-[44px] !py-0"
                        value={filters.status}
                        onChange={(val) => setFilters((prev) => ({ ...prev, status: String(val) }))}
                        options={STATUS_FILTER_OPTIONS}
                        placeholder="Статус заказа"
                      />
                    </div>

                    <div>
                      <Dropdown
                        label="Подразделение"
                        buttonClassName="h-11 min-h-[44px] !py-0"
                        value={filters.department}
                        onChange={(val) => setFilters((prev) => ({ ...prev, department: String(val) }))}
                        options={departmentOptions}
                        placeholder="Подразделение"
                      />
                    </div>

                    <div>
                      <Dropdown
                        label="Исполнитель"
                        buttonClassName="h-11 min-h-[44px] !py-0"
                        value={filters.assignee}
                        onChange={(val) => setFilters((prev) => ({ ...prev, assignee: String(val) }))}
                        options={assigneeOptions}
                        placeholder="Исполнитель"
                      />
                    </div>

                    <div>
                      <Dropdown
                        label="Тип услуги"
                        buttonClassName="h-11 min-h-[44px] !py-0"
                        value={filters.service_type}
                        onChange={(val) => setFilters((prev) => ({ ...prev, service_type: String(val) }))}
                        options={visibleServiceTypeOptions}
                        placeholder="Тип услуги"
                      />
                    </div>

                    <div>
                      <Dropdown
                        label="Тип клиента"
                        buttonClassName="h-11 min-h-[44px] !py-0"
                        value={filters.client_type}
                        onChange={(val) => setFilters((prev) => ({ ...prev, client_type: String(val) }))}
                        options={CLIENT_TYPE_OPTIONS}
                        placeholder="Тип клиента"
                      />
                    </div>

                    <div>
                      <Dropdown
                        label="Приоритет"
                        buttonClassName="h-11 min-h-[44px] !py-0"
                        value={filters.priority}
                        onChange={(val) => setFilters((prev) => ({ ...prev, priority: String(val) }))}
                        options={PRIORITY_OPTIONS}
                        placeholder="Приоритет"
                      />
                    </div>

                    <div>
                      <Dropdown
                        label="Фильтрация дат"
                        buttonClassName="h-11 min-h-[44px] !py-0"
                        value={filters.date_target}
                        onChange={(val) => setFilters((prev) => ({ ...prev, date_target: String(val) }))}
                        options={DATE_TARGET_OPTIONS}
                        placeholder="Фильтрация дат"
                      />
                    </div>
                  </div>

                  {filters.date_target === 'stage_completed' && (
                    <div className="mt-3 rounded-xl border border-blue-200/80 bg-blue-50/70 px-3 py-2 text-xs text-blue-700 dark:border-blue-900/40 dark:bg-blue-950/30 dark:text-blue-300">
                      {filters.stage && filters.stage !== 'all'
                        ? `🔍 Ищутся заказы, завершившие этап «${selectedStageOption.label.replace(/^Этап\s+«?|»?$/g, '')}»`
                        : 'Выберите этап в списке выше для точной выборки по дате завершения'}
                    </div>
                  )}

                  <div className="mt-3 flex items-center justify-between border-t border-gray-200/80 pt-3 dark:border-gray-800">
                    <label className="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
                      <input
                        type="checkbox"
                        checked={rememberSettings}
                        onChange={(e) => setRememberSettings(e.target.checked)}
                        className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900"
                      />
                      <span>Запоминать настройки журнала</span>
                    </label>

                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={resetAllFilters}
                        className="inline-flex items-center gap-1.5 rounded-xl border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-300"
                      >
                        <RotateCcw className="h-3.5 w-3.5" />
                        <span>Сбросить значения</span>
                      </button>
                      <button
                        type="button"
                        onClick={applyFilters}
                        className="inline-flex items-center gap-1.5 rounded-xl bg-blue-600 px-4 py-1.5 text-xs font-semibold text-white transition hover:bg-blue-700"
                      >
                        <span>Применить</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* Quick Filters & Local Search Section (Only in Journal Mode) */}
      {searchMode === 'journal' && (
        <section className="rounded-3xl bg-white p-5 shadow-sm dark:bg-gray-900 sm:p-6">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between gap-3">
              <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3 min-w-0">
                <button
                  type="button"
                  onClick={() => setShowQuickFilters((prev) => !prev)}
                  className="flex items-center gap-1.5 text-sm text-gray-700 hover:text-gray-900 dark:text-gray-300 dark:hover:text-gray-100 transition-colors text-left"
                >
                  <span className="text-gray-500 dark:text-gray-400">Найдено:</span>
                  <span className={`font-bold text-gray-900 dark:text-gray-100 ${selectedTotalCount === null ? 'animate-pulse' : ''}`}>
                    {selectedTotalCount === null || selectedTotalCount === undefined ? '…' : selectedTotalCount}
                  </span>
                </button>
                <span className="text-gray-300 dark:text-gray-600 hidden sm:inline">•</span>
                <button
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    cycleLocalSort();
                  }}
                  className="inline-flex items-center gap-1.5 text-xs text-gray-500 hover:text-blue-600 dark:text-gray-400 dark:hover:text-blue-300 transition-colors text-left group"
                  title="Нажмите, чтобы изменить сортировку"
                >
                  <ActiveSortIcon className="h-3.5 w-3.5 text-blue-500 group-hover:scale-110 transition-transform shrink-0" />
                  <span>Сортировка:</span>
                  <span className="font-semibold text-blue-600 dark:text-blue-400">
                    {activeSortOption.label}
                  </span>
                </button>
              </div>
              <button
                type="button"
                onClick={() => setShowQuickFilters((prev) => !prev)}
                className="inline-flex h-8 w-8 items-center justify-center rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-300 transition-colors shrink-0"
                title={showQuickFilters ? 'Свернуть плитки' : 'Развернуть плитки'}
                aria-expanded={showQuickFilters}
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${showQuickFilters ? 'rotate-180' : ''}`} />
              </button>
            </div>

            {showQuickFilters && (
              <>
                <div className="grid grid-cols-4 gap-1.5 sm:gap-2 lg:grid-cols-8">
                  {QUICK_FILTERS.map((item) => {
                    const isActive = effectiveQuickView === item.key;
                    const styles = QUICK_FILTER_STYLES[item.key] || QUICK_FILTER_STYLES.all;
                    return (
                      <button
                        key={item.key}
                        type="button"
                        onClick={() => {
                          setQuickView(item.key);
                          setFilters((prev) => ({ ...prev, status: item.key }));
                          setAppliedFilters((prev) => ({ ...prev, status: item.key }));
                        }}
                        className={`rounded-xl border px-1.5 py-1.5 sm:px-2.5 sm:py-2 text-center transition-all ${isActive ? styles.active : styles.idle}`}
                      >
                        <div className={`text-sm sm:text-base font-bold leading-tight ${orderCounts[item.key] === null ? 'animate-pulse' : ''}`}>
                          {orderCounts[item.key] === null || orderCounts[item.key] === undefined ? '…' : orderCounts[item.key]}
                        </div>
                        <div className="mt-0.5 text-[10px] sm:text-xs font-medium leading-tight truncate">{item.label}</div>
                      </button>
                    );
                  })}
                </div>

                <div className="space-y-1.5">
                  <div className="flex items-center justify-between px-1">
                    <span className="flex items-center gap-1.5 text-xs font-semibold text-gray-700 dark:text-gray-300">
                      <Search className="h-3.5 w-3.5 text-blue-600 dark:text-blue-400" />
                      Поиск по найденному
                    </span>
                    {searchQuery && (
                      <button
                        type="button"
                        onClick={() => setSearchQuery('')}
                        className="text-xs font-medium text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      >
                        Очистить
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(event) => setSearchQuery(event.target.value)}
                      placeholder="Фильтр по номеру, клиенту, телефону, адресу или исполнителю..."
                      className="w-full rounded-2xl border border-gray-200 bg-gray-50 py-2.5 pl-10 pr-4 text-sm text-gray-900 outline-none transition placeholder:text-xs placeholder:text-gray-400 focus:border-blue-300 focus:bg-white focus:ring-2 focus:ring-blue-500/20 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:placeholder:text-gray-500 dark:focus:border-blue-700 dark:focus:bg-gray-900"
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </section>
      )}

      {/* Orders List Section */}
      <section className="space-y-4">
        {loading ? (
          <OrderLogLoadingSkeleton />
        ) : visibleOrders.length === 0 ? (
          <div className="rounded-3xl bg-white p-10 text-center shadow-sm dark:bg-gray-900">
            <SearchX className="mx-auto mb-4 h-12 w-12 text-gray-300 dark:text-gray-600" />
            <div className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {searchMode === 'exact' ? 'Заказ не найден' : 'Заказы не найдены'}
            </div>
            <div className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              {searchMode === 'exact'
                ? 'Проверьте правильность введённого номера заказа или телефона.'
                : 'Попробуйте изменить интервал или ослабить фильтры.'}
            </div>
          </div>
        ) : (
          <>
            {searchMode === 'exact' && (
              <div className="flex items-center rounded-2xl bg-blue-50/80 px-4 py-3 text-xs text-blue-800 dark:bg-blue-950/40 dark:text-blue-200">
                <div className="flex items-center gap-2">
                  <Search className="h-4 w-4 shrink-0 text-blue-600 dark:text-blue-400" />
                  <span className="font-semibold">
                    Найдено по запросу:{' '}
                    <strong>{visibleOrders.length}</strong>{' '}
                    {visibleOrders.length === 1 ? 'заказ' : visibleOrders.length < 5 ? 'заказа' : 'заказов'}
                  </span>
                </div>
              </div>
            )}
            {visibleOrders.map((order) => (
              <JournalOrderCard
                key={order.id}
                order={order}
                appliedStageFilter={appliedFilters.stage}
                isAppliedExactSearchMode={isAppliedExactSearchMode}
                dateTarget={appliedFilters.date_target}
                onOpenOrder={openOrder}
                onOpenAssignModal={openAssignModal}
              />
            ))}
            <div ref={loadMoreSentinelRef} className="min-h-[2px]" aria-live="polite" aria-busy={loadingMore}>
              {loadingMore ? <OrderLogLoadingSkeleton count={2} /> : null}
            </div>
          </>
        )}
      </section>

      {/* Mobile Filters Bottom Sheet Modal */}
      <ModalShell
        open={isMobileFiltersOpen}
        onClose={() => setIsMobileFiltersOpen(false)}
        variant="sheet"
        panelClassName="max-h-[85vh] flex flex-col rounded-t-3xl bg-white dark:bg-gray-900"
      >
        <div className="flex items-center justify-between border-b border-gray-100 p-4 dark:border-gray-800">
          <div className="flex items-center gap-2">
            <div className="text-base font-bold text-gray-900 dark:text-gray-100">
              Фильтры журнала
            </div>
            {draftFilterCount > 0 && (
              <span className="rounded-full bg-blue-100 px-2 py-0.5 text-xs font-semibold text-blue-700 dark:bg-blue-950 dark:text-blue-300">
                Выбрано: {draftFilterCount}
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={resetAllFilters}
              className="text-xs font-semibold text-gray-500 hover:text-gray-700 dark:text-gray-400"
            >
              Сбросить
            </button>
            <button
              type="button"
              onClick={() => setIsMobileFiltersOpen(false)}
              className="rounded-xl p-1 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4 overscroll-contain">
          <div>
            <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
              Режим дат
            </div>
            <div className="grid grid-cols-2 gap-1 rounded-2xl bg-gray-100 p-1 dark:bg-gray-800">
              <button
                type="button"
                onClick={() => setFilters((prev) => ({ ...prev, date_mode: 'period' }))}
                className={`rounded-xl py-2 text-xs font-semibold transition ${
                  filters.date_mode !== 'date'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100'
                    : 'text-gray-500'
                }`}
              >
                Период
              </button>
              <button
                type="button"
                onClick={() => setFilters((prev) => ({ ...prev, date_mode: 'date', date_to: prev.date_from }))}
                className={`rounded-xl py-2 text-xs font-semibold transition ${
                  filters.date_mode === 'date'
                    ? 'bg-blue-600 text-white shadow-sm dark:bg-blue-500'
                    : 'text-gray-500'
                }`}
              >
                Одна дата
              </button>
            </div>
          </div>

          <div>
            <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
              Цель фильтрации
            </div>
            <div className="grid grid-cols-2 gap-1 rounded-2xl bg-gray-100 p-1 dark:bg-gray-800">
              <button
                type="button"
                onClick={() => setFilters((prev) => ({ ...prev, date_target: 'created' }))}
                className={`rounded-xl py-2 text-xs font-semibold transition ${
                  filters.date_target !== 'stage_completed'
                    ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-100'
                    : 'text-gray-500'
                }`}
              >
                Дата создания
              </button>
              <button
                type="button"
                onClick={() => setFilters((prev) => ({ ...prev, date_target: 'stage_completed' }))}
                className={`rounded-xl py-2 text-xs font-semibold transition ${
                  filters.date_target === 'stage_completed'
                    ? 'bg-blue-600 text-white shadow-sm dark:bg-blue-500'
                    : 'text-gray-500'
                }`}
              >
                Завершение этапа
              </button>
            </div>
          </div>

          <div>
            <div className="mb-2 text-xs font-semibold text-gray-700 dark:text-gray-300">
              {filters.date_mode === 'date' ? 'Дата' : 'Интервал дат'}
            </div>
            {filters.date_mode === 'date' ? (
              <DatePicker
                value={filters.date_from}
                onChange={(val) => {
                  updateDateFilter('date_from', val);
                  updateDateFilter('date_to', val);
                }}
                placeholder="Дата"
                label="Дата"
                allowPastDates
                showTodayButton={false}
                inputClassName="h-11 rounded-xl border-gray-200 bg-white text-xs dark:border-gray-700 dark:bg-gray-900"
              />
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <DatePicker
                  value={filters.date_from}
                  onChange={(val) => updateDateFilter('date_from', val)}
                  placeholder="От"
                  label="От"
                  allowPastDates
                  showTodayButton={false}
                  inputClassName="h-11 rounded-xl border-gray-200 bg-white text-xs dark:border-gray-700 dark:bg-gray-900"
                />
                <DatePicker
                  value={filters.date_to}
                  onChange={(val) => updateDateFilter('date_to', val)}
                  placeholder="До"
                  label="До"
                  allowPastDates
                  showTodayButton={false}
                  inputClassName="h-11 rounded-xl border-gray-200 bg-white text-xs dark:border-gray-700 dark:bg-gray-900"
                />
              </div>
            )}
            {filters.date_target === 'stage_completed' && (
              <div className="mt-2 rounded-xl bg-blue-50 p-2 text-[11px] text-blue-700 dark:bg-blue-950/40 dark:text-blue-300">
                {filters.stage && filters.stage !== 'all'
                  ? `Поиск заказов, завершивших «${selectedStageOption.label.replace(/^Этап\s+«?|»?$/g, '')}»`
                  : 'Выберите этап ниже, чтобы увидеть завершившие его заказы'}
              </div>
            )}
          </div>

          <div className="space-y-3">
            <Dropdown
              label="Этап / поток"
              buttonClassName="h-11 min-h-[44px] !py-0"
              value={filters.stage}
              onChange={(val) => setFilters((prev) => ({ ...prev, stage: String(val), assignee: 'all' }))}
              options={visibleStageFilterOptions}
              placeholder="Этап или поток"
            />
            <Dropdown
              label="Статус заказа"
              buttonClassName="h-11 min-h-[44px] !py-0"
              value={filters.status}
              onChange={(val) => setFilters((prev) => ({ ...prev, status: String(val) }))}
              options={STATUS_FILTER_OPTIONS}
              placeholder="Статус заказа"
            />
            <Dropdown
              label="Подразделение"
              buttonClassName="h-11 min-h-[44px] !py-0"
              value={filters.department}
              onChange={(val) => setFilters((prev) => ({ ...prev, department: String(val) }))}
              options={departmentOptions}
              placeholder="Подразделение"
            />
            <Dropdown
              label="Исполнитель"
              buttonClassName="h-11 min-h-[44px] !py-0"
              value={filters.assignee}
              onChange={(val) => setFilters((prev) => ({ ...prev, assignee: String(val) }))}
              options={assigneeOptions}
              placeholder="Исполнитель"
            />
            <Dropdown
              label="Тип услуги"
              buttonClassName="h-11 min-h-[44px] !py-0"
              value={filters.service_type}
              onChange={(val) => setFilters((prev) => ({ ...prev, service_type: String(val) }))}
              options={visibleServiceTypeOptions}
              placeholder="Тип услуги"
            />
            <Dropdown
              label="Тип клиента"
              buttonClassName="h-11 min-h-[44px] !py-0"
              value={filters.client_type}
              onChange={(val) => setFilters((prev) => ({ ...prev, client_type: String(val) }))}
              options={CLIENT_TYPE_OPTIONS}
              placeholder="Тип клиента"
            />
            <Dropdown
              label="Приоритет"
              buttonClassName="h-11 min-h-[44px] !py-0"
              value={filters.priority}
              onChange={(val) => setFilters((prev) => ({ ...prev, priority: String(val) }))}
              options={PRIORITY_OPTIONS}
              placeholder="Приоритет"
            />
          </div>

          <div className="pt-2">
            <label className="inline-flex items-center gap-2 text-xs text-gray-600 dark:text-gray-300">
              <input
                type="checkbox"
                checked={rememberSettings}
                onChange={(e) => setRememberSettings(e.target.checked)}
                className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-900"
              />
              <span>Запоминать настройки журнала</span>
            </label>
          </div>
        </div>

        <div className="border-t border-gray-100 p-4 dark:border-gray-800">
          <button
            type="button"
            onClick={applyFilters}
            className="h-12 w-full rounded-2xl bg-blue-600 font-semibold text-white shadow-md transition hover:bg-blue-700"
          >
            Применить фильтры
          </button>
        </div>
      </ModalShell>

      <ModalShell open={!!assignModalOrder} onClose={() => { if (!assignmentSaving) closeAssignModal(); }} variant="window" panelClassName="w-full max-w-lg rounded-3xl bg-white p-5 dark:bg-gray-900">
        <div className="space-y-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-lg font-semibold text-gray-950 dark:text-gray-50">Назначение исполнителя</div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">Заказ № {assignModalOrder?.order_number || '-'}</div>
            </div>
            <button type="button" onClick={closeAssignModal} disabled={assignmentSaving} className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-800 dark:hover:text-gray-300"><X className="h-5 w-5" /></button>
          </div>

          {assigneesLoading ? (
            <div className="rounded-2xl bg-gray-50 p-3 dark:bg-gray-800/70">
              <ModalListSkeleton count={4} />
            </div>
          ) : (
            <>
              {isFlowAssigneeSelection && selectedAssigneeFlow ? (
                <div className="space-y-3">
                  <div className="text-sm font-medium text-gray-700 dark:text-gray-200">Этап / поток</div>
                  <div className={`grid gap-2 ${availableAssigneeFlows.length > 2 ? 'grid-cols-1 sm:grid-cols-3' : availableAssigneeFlows.length > 1 ? 'sm:grid-cols-2' : 'grid-cols-1'}`}>
                    {availableAssigneeFlows.map((flow) => {
                      const flowConfig = STAGES_CONFIG[flow.stage_name] || {};
                      const FlowIcon = flowConfig.icon || UserCheck;
                      const selected = flow.stage_name === selectedAssigneeFlow.stage_name;
                      const isReworkFlow = Boolean(assignModalOrder?.active_rework) && ['washing_laundry', 'washing_cleaning'].includes(flow.stage_name);
                      return (
                        <button key={flow.stage_name} type="button" onClick={() => setAssigneeStageDraft(flow.stage_name)} disabled={assignmentSaving} className={`rounded-2xl border px-3 py-3 text-left transition-colors ${selected ? 'border-blue-300 bg-blue-50 text-blue-700 dark:border-blue-800 dark:bg-blue-950/30 dark:text-blue-300' : 'border-gray-200 bg-white text-gray-700 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-200 dark:hover:bg-gray-800'}`}>
                          <div className="flex items-center gap-2">
                            <span className="relative flex shrink-0 items-center">
                              <FlowIcon className="h-4 w-4" />
                              {isReworkFlow && (
                                <RotateCcw className="absolute -bottom-0.5 -right-1 h-2.5 w-2.5 text-amber-600 dark:text-amber-400" />
                              )}
                            </span>
                            <span className="font-semibold">{flow.label}</span>
                            {isReworkFlow && (
                              <span className="rounded-md bg-amber-100 px-1 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-950/50 dark:text-amber-300">
                                доработка
                              </span>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">{flow.assignee_name || 'Не назначен'}</div>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : null}

              <div className="space-y-2">
                <Dropdown label={isFlowAssigneeSelection ? `Исполнитель · ${selectedAssigneeFlow?.label || 'поток'}` : 'Исполнитель этапа'} value={assigneeDraft} onChange={(value) => setAssigneeDraft(String(value))} options={[{ value: UNASSIGN_ASSIGNEE_VALUE, label: isFlowAssigneeSelection ? `Снять исполнителя · ${selectedAssigneeFlow?.label || 'поток'}` : 'Снять исполнителя', icon: XCircle }, ...selectedAssigneeUsers.map((user) => ({ value: String(user.id), label: user.name || user.login || `user:${user.id}`, icon: UserCheck }))]} placeholder={isFlowAssigneeSelection ? `Выберите исполнителя для ${selectedAssigneeFlow?.label ? selectedAssigneeFlow.label.toLowerCase() : 'потока'}` : 'Выберите исполнителя'} />
                {selectedAssigneeUsers.length === 0 && selectedAssigneeCurrentValue === UNASSIGN_ASSIGNEE_VALUE && <div className="rounded-2xl bg-gray-50 px-3 py-4 text-sm text-gray-500 dark:bg-gray-800/70 dark:text-gray-400">Нет доступных исполнителей для этого этапа или потока.</div>}
              </div>

              <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button type="button" onClick={closeAssignModal} disabled={assignmentSaving} className="inline-flex h-11 items-center justify-center rounded-2xl border border-gray-200 px-4 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">Отмена</button>
                <button type="button" onClick={saveAssignee} disabled={assigneeSaveDisabled} className="inline-flex h-11 items-center justify-center rounded-2xl bg-blue-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">{assignmentSaving ? 'Сохраняю...' : 'Сохранить'}</button>
              </div>
            </>
          )}
        </div>
      </ModalShell>

      <ModalShell open={printModalOpen} onClose={() => { if (!printLoading) closePrintModal(); }} variant="window" panelClassName="w-full max-w-xl overflow-visible rounded-3xl bg-white p-5 dark:bg-gray-900 sm:p-6">
        <div className="flex flex-col gap-5">
          <div className="flex items-start justify-between gap-4">
            <div className="flex min-w-0 items-start gap-3">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950/40 dark:text-blue-300">
                <Printer className="h-5 w-5" />
              </span>
              <div className="min-w-0">
                <div className="text-lg font-semibold text-gray-950 dark:text-gray-50">Печать листов</div>
              </div>
            </div>
            <button type="button" onClick={closePrintModal} disabled={printLoading} aria-label="Закрыть" className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-800 dark:hover:text-gray-300"><X className="h-5 w-5" /></button>
          </div>

          <div className="grid grid-cols-2 gap-2 rounded-2xl bg-gray-100 p-1 dark:bg-gray-800">
            {[
              { value: 'work', label: 'Рабочий лист', icon: ClipboardList },
              { value: 'assembly', label: 'Сборочный лист', icon: Package }
            ].map((option) => {
              const OptionIcon = option.icon;
              const selected = printDocumentTypeDraft === option.value;
              return (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    haptic('selection');
                    resetPreparedPrint();
                    setPrintDocumentTypeDraft(option.value);
                  }}
                  disabled={printLoading}
                  className={`flex min-h-11 items-center justify-center gap-2 rounded-xl px-3 text-sm font-semibold transition-colors ${
                    selected
                      ? 'bg-white text-blue-700 shadow-sm dark:bg-gray-700 dark:text-blue-300'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  <OptionIcon className="h-4 w-4" />
                  <span>{option.label}</span>
                </button>
              );
            })}
          </div>

          {printDocumentTypeDraft === 'work' && (
            <>
              <div className="space-y-2">
                <Dropdown
                  label="Этап / поток"
                  value={printStageDraft}
                  onChange={(value) => {
                    resetPreparedPrint();
                    setPrintStageDraft(String(value));
                  }}
                  options={visiblePrintStageOptions}
                  placeholder="Выберите этап"
                  disabled={printLoading}
                />
              </div>

              <div className="space-y-2">
                <Dropdown
                  label="Статус заказа"
                  value={printOrderStatusesDraft}
                  onChange={(values) => {
                    resetPreparedPrint();
                    setPrintOrderStatusesDraft(values.map(value => String(value)));
                  }}
                  options={PRINT_ORDER_STATUS_OPTIONS}
                  placeholder="Выберите статус"
                  disabled={printLoading}
                  multiple
                />
              </div>

              <button
                type="button"
                role="switch"
                aria-checked={printCompactDraft}
                onClick={() => {
                  haptic('selection');
                  resetPreparedPrint();
                  setPrintCompactDraft((current) => !current);
                }}
                disabled={printLoading}
                className={`flex items-center justify-between gap-4 rounded-2xl border px-4 py-3 text-left transition-colors disabled:opacity-50 ${
                  printCompactDraft
                    ? 'border-blue-300 bg-blue-50 dark:border-blue-800 dark:bg-blue-950/30'
                    : 'border-gray-200 bg-gray-50/80 hover:border-gray-300 dark:border-gray-700 dark:bg-gray-800/60 dark:hover:border-gray-600'
                }`}
              >
                <span className="min-w-0">
                  <span className={`block text-sm font-semibold ${printCompactDraft ? 'text-blue-800 dark:text-blue-200' : 'text-gray-800 dark:text-gray-100'}`}>Компактный вид</span>
                </span>
                <span className={`relative inline-flex h-6 w-11 shrink-0 rounded-full transition-colors ${printCompactDraft ? 'bg-blue-600' : 'bg-gray-300 dark:bg-gray-600'}`} aria-hidden="true">
                  <span className={`absolute top-1 h-4 w-4 rounded-full bg-white shadow-sm transition-transform ${printCompactDraft ? 'translate-x-6' : 'translate-x-1'}`} />
                </span>
              </button>
            </>
          )}

          {printDocumentTypeDraft === 'assembly' && (
            <div className="space-y-2">
              <Dropdown
                label="Сортировка сборочного листа"
                value={printAssemblySortDraft}
                onChange={(value) => {
                  resetPreparedPrint();
                  setPrintAssemblySortDraft(String(value));
                }}
                options={ASSEMBLY_SORT_OPTIONS}
                placeholder="Выберите сортировку"
                disabled={printLoading}
              />
              <div className="text-xs text-gray-500 dark:text-gray-400">
                Порядок предметов внутри заказа сохраняется.
              </div>
            </div>
          )}

          <div className="space-y-3">
            <div className="flex rounded-2xl bg-gray-100 p-1 dark:bg-gray-800">
              {[
                { value: 'date', label: 'Один день' },
                { value: 'period', label: 'Период' }
              ].map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => {
                    resetPreparedPrint();
                    setPrintRangeMode(option.value);
                  }}
                  disabled={printLoading}
                  className={`h-9 flex-1 rounded-xl px-3 text-sm font-semibold transition-colors ${
                    printRangeMode === option.value
                      ? 'bg-white text-gray-900 shadow-sm dark:bg-gray-700 dark:text-gray-50'
                      : 'text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>

            <div className={`grid gap-3 ${printRangeMode === 'period' ? 'grid-cols-2' : 'grid-cols-1'}`}>
              <div>
                <DatePicker
                  value={printDateFrom}
                  onChange={(value) => {
                    resetPreparedPrint();
                    setPrintDateFrom(value);
                    if (printRangeMode === 'date') setPrintDateTo(value);
                  }}
                  disabled={printLoading}
                  placeholder={printRangeMode === 'period' ? 'Дата от' : 'Дата'}
                  allowPastDates
                  showTodayButton
                  inputClassName="min-h-[44px] rounded-xl border-gray-200 bg-white text-sm dark:border-gray-700 dark:bg-gray-900"
                />
              </div>
              {printRangeMode === 'period' && (
                <div>
                  <DatePicker
                    value={printDateTo}
                    onChange={(value) => {
                      resetPreparedPrint();
                      setPrintDateTo(value);
                    }}
                    disabled={printLoading}
                    placeholder="Дата до"
                    allowPastDates
                    showTodayButton
                    inputClassName="min-h-[44px] rounded-xl border-gray-200 bg-white text-sm dark:border-gray-700 dark:bg-gray-900"
                  />
                </div>
              )}
            </div>
            <div className={`text-xs ${printRangeDays < 1 || printRangeDays > MAX_RANGE_DAYS ? 'text-red-600 dark:text-red-400' : 'text-gray-500 dark:text-gray-400'}`}>
              {printDocumentTypeDraft === 'assembly'
                ? 'Заказы отбираются по запланированной дате доставки клиенту.'
                : printStageDraft === 'cleaning'
                ? 'Заказы отбираются по дате назначения выезда.'
                : 'Заказы отбираются по актуальной дате забора от клиента.'}
              {printRangeMode === 'period' && printRangeDays > 0 ? ` Выбрано дней: ${printRangeDays}.` : ''}
            </div>
          </div>

          {printLoading && (
            <div className="rounded-2xl border border-blue-200 bg-blue-50 px-4 py-3 dark:border-blue-900/70 dark:bg-blue-950/25">
              <div className="text-sm font-semibold text-blue-900 dark:text-blue-100">Формируем PDF...</div>
              <div className="mt-2 h-2 overflow-hidden rounded-full bg-blue-100 dark:bg-blue-950/70">
                <div className="order-print-progress-indeterminate h-full rounded-full bg-blue-600 dark:bg-blue-400" />
              </div>
              <div className="mt-2 text-xs text-blue-700 dark:text-blue-300">Ожидаем готовый файл от сервера.</div>
            </div>
          )}

          {!printLoading && printObjectUrl && (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-4 dark:border-emerald-900/70 dark:bg-emerald-950/25">
              <div className="flex items-center gap-2 text-sm font-semibold text-emerald-900 dark:text-emerald-100">
                <CheckCircle className="h-4 w-4" />
                PDF готов
              </div>
              <div className="mt-1 truncate text-xs text-emerald-700 dark:text-emerald-300">{printFileName}</div>
              <div className="mt-3 flex items-center justify-between gap-3">
                <div className="text-sm font-medium text-emerald-800 dark:text-emerald-200">
                  {printDocumentTypeDraft === 'assembly' ? 'Найдено предметов' : 'Найдено заданий'}: <span className="font-bold">{printTaskCount ?? '—'}</span>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  <button type="button" onClick={openPreparedWorkSheet} title="Открыть PDF" aria-label="Открыть PDF" className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 bg-white text-emerald-800 transition-colors hover:bg-emerald-100 dark:border-emerald-900 dark:bg-gray-900 dark:text-emerald-300 dark:hover:bg-emerald-950/40">
                    <ExternalLink className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={savePreparedWorkSheet} title="Сохранить PDF" aria-label="Сохранить PDF" className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-emerald-200 bg-white text-emerald-800 transition-colors hover:bg-emerald-100 dark:border-emerald-900 dark:bg-gray-900 dark:text-emerald-300 dark:hover:bg-emerald-950/40">
                    <Download className="h-4 w-4" />
                  </button>
                  <button type="button" onClick={printPreparedWorkSheet} title="Печатать PDF" aria-label="Печатать PDF" className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-emerald-600 text-white transition-colors hover:bg-emerald-700">
                    <Printer className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col-reverse gap-2 border-t border-gray-200 pt-4 dark:border-gray-800 sm:flex-row sm:justify-end">
            <button type="button" onClick={closePrintModal} disabled={printLoading} className="inline-flex h-11 items-center justify-center rounded-2xl border border-gray-200 px-4 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">Закрыть</button>
            {!printObjectUrl && (
              <button type="button" onClick={prepareOrderWorkSheet} disabled={printSubmitDisabled} className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-blue-600 px-5 text-sm font-semibold text-white transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                {printLoading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" /> : <Printer className="h-4 w-4" />}
                {printLoading ? 'Формируем PDF...' : 'Сформировать PDF'}
              </button>
            )}
          </div>
        </div>
      </ModalShell>

      <ModalShell open={bulkModalOpen} onClose={() => { if (!bulkSaving) closeBulkModal(); }} variant="window" panelClassName="max-h-[calc(100dvh-2rem)] w-full max-w-lg overflow-visible rounded-3xl bg-white p-5 dark:bg-gray-900">
        <div className="flex max-h-[calc(100dvh-4.5rem)] min-h-0 flex-col gap-4">
          <div className="flex shrink-0 items-start justify-between gap-4">
            <div>
              <div className="text-lg font-semibold text-gray-950 dark:text-gray-50">Массовое снятие назначений</div>
              <div className="mt-1 text-sm text-gray-500 dark:text-gray-400">Можно снять назначения одного этапа или сразу всех этапов выбранного исполнителя.</div>
            </div>
            <button type="button" onClick={closeBulkModal} disabled={bulkSaving} className="rounded-xl p-2 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 disabled:opacity-50 dark:hover:bg-gray-800 dark:hover:text-gray-300"><X className="h-5 w-5" /></button>
          </div>

          {bulkOptionsLoading ? (
            <div className="rounded-2xl bg-gray-50 p-3 dark:bg-gray-800/70">
              <ModalListSkeleton count={4} />
            </div>
          ) : bulkStages.length === 0 ? (
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-5 text-sm text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300">
              Активных назначений для массового снятия нет.
            </div>
          ) : (
            <div className="flex min-h-0 flex-1 flex-col gap-4">
              <div className="shrink-0 space-y-2">
                <Dropdown
                  label="Этап / поток"
                  value={bulkStageDraft}
                  onChange={(value) => {
                    setBulkStageDraft(String(value));
                    setBulkAssigneeDraft('');
                  }}
                  options={bulkStageOptions}
                  placeholder="Выберите этап или поток"
                />
              </div>

              <div className="shrink-0 space-y-2">
                <Dropdown
                  label="Исполнитель"
                  disabled={!selectedBulkStage}
                  value={bulkAssigneeDraft}
                  onChange={(value) => setBulkAssigneeDraft(String(value))}
                  options={bulkAssigneeOptions}
                  placeholder="Выберите исполнителя"
                />
              </div>

              {selectedBulkAssignee && (
                <>
                  {Array.isArray(selectedBulkAssignee.orders) && selectedBulkAssignee.orders.length > 0 && (
                    <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain rounded-2xl border border-gray-200 dark:border-gray-700">
                      {selectedBulkAssignee.orders.map((order, index) => (
                        <div key={order.id || index} className="border-b border-gray-100 px-3 py-2.5 last:border-0 dark:border-gray-700">
                          <div className="flex items-center justify-between gap-3 text-sm">
                            <span className="font-medium text-gray-900 dark:text-gray-100">№{order.order_number || order.id}</span>
                            <span className="shrink-0 text-xs text-gray-500 dark:text-gray-400">{order.status_label || order.status}</span>
                          </div>
                          <div className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {order.department_name || 'Подразделение не выбрано'}
                            {Array.isArray(order.stage_labels) && order.stage_labels.length > 0 ? ` · ${order.stage_labels.join(', ')}` : ''}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}

              <div className="mt-auto flex shrink-0 flex-col-reverse gap-2 sm:flex-row sm:justify-end">
                <button type="button" onClick={closeBulkModal} disabled={bulkSaving} className="inline-flex h-11 items-center justify-center rounded-2xl border border-gray-200 px-4 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">Отмена</button>
                <button type="button" onClick={requestBulkUnassignConfirmation} disabled={bulkSaving || !selectedBulkAssignee} className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50">
                  <UserMinus className="h-4 w-4" />
                  {bulkSaving ? 'Снимаю...' : `Снять${selectedBulkAssignee ? ` (${selectedBulkAssignee.assignments_count})` : ''}`}
                </button>
              </div>
            </div>
          )}
        </div>
      </ModalShell>

      <ModalShell
        open={bulkConfirmOpen}
        onClose={() => { if (!bulkSaving) setBulkConfirmOpen(false); }}
        variant="window"
        zIndexClass="z-[60]"
        panelClassName="w-full max-w-md rounded-3xl bg-white p-5 dark:bg-gray-900"
      >
        <div className="space-y-5">
          <div className="flex items-start gap-3">
            <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-red-100 text-red-600 dark:bg-red-950/50 dark:text-red-300">
              <UserMinus className="h-5 w-5" />
            </span>
            <div className="min-w-0">
              <div className="text-lg font-semibold text-gray-950 dark:text-gray-50">Подтвердить снятие?</div>
              <div className="mt-1 text-sm leading-5 text-gray-500 dark:text-gray-400">
                {selectedBulkAssignee?.all_assignees
                  ? 'Снять назначения у всех исполнителей выбранного этапа?'
                  : 'Снять назначения у выбранного исполнителя?'}
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-gray-50 px-4 py-3 text-sm dark:bg-gray-800/70">
            <div className="flex items-center justify-between gap-3">
              <span className="text-gray-500 dark:text-gray-400">Этап</span>
              <span className="text-right font-semibold text-gray-900 dark:text-gray-100">{selectedBulkStage?.label || '—'}</span>
            </div>
            <div className="mt-2 flex items-center justify-between gap-3">
              <span className="text-gray-500 dark:text-gray-400">Назначений</span>
              <span className="font-semibold text-gray-900 dark:text-gray-100">{selectedBulkAssignee?.assignments_count || 0}</span>
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
            <button type="button" onClick={() => setBulkConfirmOpen(false)} disabled={bulkSaving} className="inline-flex h-11 items-center justify-center rounded-2xl border border-gray-200 px-4 text-sm font-semibold text-gray-600 transition-colors hover:bg-gray-50 disabled:opacity-50 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-800">Отмена</button>
            <button type="button" onClick={saveBulkUnassign} disabled={bulkSaving || !selectedBulkAssignee} className="inline-flex h-11 items-center justify-center gap-2 rounded-2xl bg-red-600 px-4 text-sm font-semibold text-white transition-colors hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-50">
              <UserMinus className="h-4 w-4" />
              {bulkSaving ? 'Снимаю...' : 'Да, снять назначения'}
            </button>
          </div>
        </div>
      </ModalShell>

    </div>
  );
};

export default OrderLogScreen;
